import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit, Trash, Search, Filter, X, Package, Info, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, RefreshCw, Calendar, TrendingUp, TrendingDown, DollarSign, Weight, FileText, ChevronDown, Save, Minus, AlertCircle, Eye, Printer, Upload, Download, FileSpreadsheet, CheckCircle, XCircle, Users, Copy } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const XuatNhapKhoDominoManagement = () => {
  // State Management
  const [phieuList, setPhieuList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [khoList, setKhoList] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  const [currentPhieu, setCurrentPhieu] = useState({
    'SOPHIEU': '',
    'NGHIEP_VU': 'NHAP',
    'NGAYNHAP_XUAT': new Date().toISOString().split('T')[0],
    'NCC_KHACHHANG': '',
    'KHOXUAT': '',
    'KHONHAP': '',
    'NGUOIPHUTRACH': '',
    'THANHTIEN': 0,
    'PT VAT': 0,
    'TIEN VAT': 0,
    'TONGTIEN': 0,
    'DIENGIAI': ''
  });

  const [chiTietList, setChiTietList] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [phieuToDelete, setPhieuToDelete] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [originalSoPhieu, setOriginalSoPhieu] = useState('');

  // Import/Export states
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importNghiepVu, setImportNghiepVu] = useState('NHAP');
  const [isImporting, setIsImporting] = useState(false);

  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [phieuToPrint, setPhieuToPrint] = useState(null);
  const [printWithPrice, setPrintWithPrice] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('HOANG_NGUYEN_LONG');

  const fileInputRef = useRef(null);

  // Autocomplete states
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const customerDropdownRef = useRef(null);

  // Filter states
  const [filterNghiepVu, setFilterNghiepVu] = useState('ALL');
  const [filterCustomer, setFilterCustomer] = useState('ALL');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Tiêu chuẩn và Đội hàng kho
  const [tieuChuanList, setTieuChuanList] = useState([]);
  const [doiHangKhoList, setDoiHangKhoList] = useState([]);
  const [showTieuChuanModal, setShowTieuChuanModal] = useState(false);
  const [showDoiHangKhoModal, setShowDoiHangKhoModal] = useState(false);
  const [newTieuChuan, setNewTieuChuan] = useState('');
  const [newDoiHangKho, setNewDoiHangKho] = useState('');

  // Additional fields for Domino (XUẤT KHO only)
  const [klXeVaHang, setKlXeVaHang] = useState(0);
  const [klXe, setKlXe] = useState(0);
  const [klHang, setKlHang] = useState(0);
  const [soKien, setSoKien] = useState(0);
  const [soKgTbKien, setSoKgTbKien] = useState(0);

  // ==================== FETCH FUNCTIONS ====================

  const fetchPhieu = async () => {
    setIsLoading(true);
    try {
      const response = await authUtils.apiRequestKHO('XUATNHAPKHO_DOMINO', 'Find', {});
      setPhieuList(response || []);
    } catch (error) {
      console.error('Error fetching phieu:', error);
      toast.error('Lỗi khi tải danh sách phiếu');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await authUtils.apiRequestKHO('DSKH', 'Find', {});
      setCustomers(response || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Lỗi khi tải danh sách khách hàng');
    }
  };

  const fetchKho = async () => {
    try {
      const response = await authUtils.apiRequestKHO('CD_KHO', 'Find', {});
      setKhoList(response || []);
    } catch (error) {
      console.error('Error fetching kho:', error);
      toast.error('Lỗi khi tải danh sách kho');
    }
  };

  const fetchTieuChuan = async () => {
    try {
      const response = await authUtils.apiRequestKHO('CD_TIEUCHUAN', 'Find', {});
      setTieuChuanList(response || []);
    } catch (error) {
      console.error('Error fetching tieu chuan:', error);
      toast.error('Lỗi khi tải danh sách tiêu chuẩn');
    }
  };

  const fetchDoiHangKho = async () => {
    try {
      const response = await authUtils.apiRequestKHO('CD_DOIHANGKHO', 'Find', {});
      setDoiHangKhoList(response || []);
    } catch (error) {
      console.error('Error fetching doi hang kho:', error);
      toast.error('Lỗi khi tải danh sách đội hàng khô');
    }
  };

  const fetchCurrentUser = () => {
    const user = authUtils.getUserData();
    setCurrentUser(user);
  };

  useEffect(() => {
    fetchPhieu();
    fetchCustomers();
    fetchKho();
    fetchTieuChuan();
    fetchDoiHangKho();
    fetchCurrentUser();
  }, []);

  // ==================== CALCULATION FUNCTIONS ====================

  // Tính toán cho NHẬP KHO
  const calculateNhapKho = (chiTiet) => {
    const soKien = parseFloat(chiTiet['SO KIEN']) || 0;
    const soKgTbKien = parseFloat(chiTiet['SO KG TB/KIEN']) || 450;
    const soKgTbM3 = parseFloat(chiTiet['SO KG TB/M3']) || 750;

    const tongKgUl = soKien * soKgTbKien;
    const tongM3Ul = tongKgUl / soKgTbM3;
    const donGia = parseFloat(chiTiet['DONGIA']) || 0;
    const thanhTien = tongKgUl * donGia;

    return {
      'TONG KG UL': tongKgUl,
      'TONG M3 UL': tongM3Ul,
      'THANHTIEN': thanhTien
    };
  };

  // Tính toán cho XUẤT KHO - Sử dụng Số KG TB/kiện từ header
  const calculateXuatKho = (chiTiet, headerSoKgTbKien) => {
    const soKien = parseFloat(chiTiet['SO KIEN']) || 0;
    const khoiLuong = soKien * headerSoKgTbKien;
    const donGia = parseFloat(chiTiet['DONGIA']) || 0;
    const thanhTien = khoiLuong * donGia;

    return {
      'TONG KG UL': khoiLuong,
      'THANHTIEN': thanhTien
    };
  };

  // ==================== GENERATE FUNCTIONS ====================

  const generateSoPhieu = (nghiepVu, ngayNhapXuat) => {
    const date = new Date(ngayNhapXuat);
    const yy = date.getFullYear().toString().slice(-2);
    const mm = (date.getMonth() + 1).toString().padStart(2, '0');
    const dd = date.getDate().toString().padStart(2, '0');
    const prefix = nghiepVu === 'NHAP' ? 'DMN-NHAP' : 'DMN-XUAT';
    const yearMonthDay = `${yy}${mm}${dd}`;

    const phieuCungLoai = phieuList.filter(p => {
      return p['SOPHIEU'] && p['SOPHIEU'].startsWith(`${prefix}-`);
    });

    let maxNumber = 0;
    phieuCungLoai.forEach(p => {
      const match = p['SOPHIEU'].match(/-(\d+)$/);
      if (match) {
        const num = parseInt(match[1]);
        if (num > maxNumber) maxNumber = num;
      }
    });

    const nextNumber = (maxNumber + 1).toString().padStart(4, '0');
    return `${prefix}-${yearMonthDay}-${nextNumber}`;
  };

  const updateTotals = (list) => {
    const thanhTienTong = list.reduce((sum, item) => sum + (parseFloat(item['THANHTIEN']) || 0), 0);
    const ptVat = parseFloat(currentPhieu['PT VAT']) || 0;
    const tienVat = Math.round(thanhTienTong * (ptVat / 100));
    const tongTien = thanhTienTong + tienVat;

    setCurrentPhieu(prev => ({
      ...prev,
      'THANHTIEN': thanhTienTong,
      'TIEN VAT': tienVat,
      'TONGTIEN': tongTien
    }));
  };

  // ==================== HANDLE FUNCTIONS ====================

  const handleInputChange = (field, value) => {
    setCurrentPhieu(prev => {
      const newState = {
        ...prev,
        [field]: value
      };

      // Tự động tính toán lại khi thay đổi PT VAT
      if (field === 'PT VAT') {
        const thanhTien = parseFloat(prev['THANHTIEN']) || 0;
        const ptVat = parseFloat(value) || 0;
        const tienVat = Math.round(thanhTien * (ptVat / 100));
        const tongTien = thanhTien + tienVat;

        newState['TIEN VAT'] = tienVat;
        newState['TONGTIEN'] = tongTien;
      }

      return newState;
    });

    // Tự động cập nhật số phiếu khi thay đổi nghiệp vụ hoặc ngày (chỉ khi tạo mới)
    if ((field === 'NGHIEP_VU' || field === 'NGAYNHAP_XUAT') && !isEditMode) {
      const nghiepVu = field === 'NGHIEP_VU' ? value : currentPhieu['NGHIEP_VU'];
      const ngayNhapXuat = field === 'NGAYNHAP_XUAT' ? value : currentPhieu['NGAYNHAP_XUAT'];
      const newSoPhieu = generateSoPhieu(nghiepVu, ngayNhapXuat);
      setCurrentPhieu(prev => ({
        ...prev,
        'SOPHIEU': newSoPhieu
      }));
    }

    // Xử lý thay đổi nghiệp vụ
    if (field === 'NGHIEP_VU') {
      const defaultKho = khoList.length > 0 ? khoList[0]['KHO'] : '';

      let customerValue = currentPhieu['NCC_KHACHHANG'];

      if (value === 'NHAP') {
        if (!customerValue || customerValue.trim() === '') {
          const defaultCustomer = customers.find(c =>
            c['TEN_KHACHHANG'] === 'XƯỞNG CƯA HOÀNG NGUYÊN LONG' ||
            c['MA_KH'] === 'NCC-HNL-0001' ||
            (c['TEN_KHACHHANG'] && c['TEN_KHACHHANG'].includes('XƯỞNG CƯA HOÀNG NGUYÊN LONG'))
          );
          customerValue = defaultCustomer ? defaultCustomer['TEN_KHACHHANG'] : '';
          setCustomerSearchTerm(customerValue);
        }
      } else if (value === 'XUAT') {
        customerValue = '';
        setCustomerSearchTerm('');
      }

      setCurrentPhieu(prev => ({
        ...prev,
        'NCC_KHACHHANG': customerValue,
        'KHONHAP': value === 'NHAP' ? defaultKho : '',
        'KHOXUAT': value === 'XUAT' ? defaultKho : ''
      }));
    }
  };

  const handleAddPhieu = () => {
    setIsEditMode(false);

    const newSoPhieu = generateSoPhieu('NHAP', new Date().toISOString().split('T')[0]);
    const nguoiPhuTrach = currentUser ? (currentUser['Họ và Tên'] || currentUser.username) : '';

    const defaultKho = khoList.length > 0 ? khoList[0]['KHO'] : '';

    // Tìm khách hàng mặc định cho NHẬP KHO
    const defaultCustomer = customers.find(c =>
      c['TEN_KHACHHANG'] === 'XƯỞNG CƯA HOÀNG NGUYÊN LONG' ||
      c['MA_KH'] === 'NCC-HNL-0001' ||
      (c['TEN_KHACHHANG'] && c['TEN_KHACHHANG'].includes('XƯỞNG CƯA HOÀNG NGUYÊN LONG'))
    );
    const defaultCustomerName = defaultCustomer ? defaultCustomer['TEN_KHACHHANG'] : '';

    setCurrentPhieu({
      'SOPHIEU': newSoPhieu,
      'NGHIEP_VU': 'NHAP',
      'NGAYNHAP_XUAT': new Date().toISOString().split('T')[0],
      'NCC_KHACHHANG': defaultCustomerName,
      'KHOXUAT': '',
      'KHONHAP': defaultKho,
      'NGUOIPHUTRACH': nguoiPhuTrach,
      'THANHTIEN': 0,
      'PT VAT': 0,
      'TIEN VAT': 0,
      'TONGTIEN': 0,
      'DIENGIAI': ''
    });

    setChiTietList([]);
    setKlXeVaHang(0);
    setKlXe(0);
    setKlHang(0);
    setSoKien(0);
    setSoKgTbKien(0);
    setShowModal(true);
    setCustomerSearchTerm(defaultCustomerName);
  };

  const handleEditPhieu = async (phieu) => {
    setIsEditMode(true);
    setOriginalSoPhieu(phieu['SOPHIEU']);

    // Format lại ngày để hiển thị đúng trong input type="date"
    let formattedDate = '';
    if (phieu['NGAYNHAP_XUAT']) {
      try {
        const dateObj = new Date(phieu['NGAYNHAP_XUAT']);
        if (!isNaN(dateObj.getTime())) {
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        }
      } catch (e) {
        console.error('Error formatting date:', e);
      }
    }

    setCurrentPhieu({
      ...phieu,
      'NGAYNHAP_XUAT': formattedDate
    });
    setCustomerSearchTerm(phieu['NCC_KHACHHANG'] || '');

    // Set additional fields
    setKlXeVaHang(parseFloat(phieu['KL XE VA HANG']) || 0);
    setKlXe(parseFloat(phieu['KL XE']) || 0);
    setKlHang(parseFloat(phieu['KL HANG']) || 0);
    setSoKien(parseFloat(phieu['SO KIEN']) || 0);
    setSoKgTbKien(parseFloat(phieu['SO KG TB/KIEN']) || 0);

    setShowModal(true);

    try {
      const response = await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET_DOMINO', 'Find', {
        "Where": `SOPHIEU = '${phieu['SOPHIEU']}'`
      });
      // Filter client-side để đảm bảo chỉ lấy chi tiết của phiếu này (đề phòng API trả về tất cả)
      const filteredDetails = (response || []).filter(item => item['SOPHIEU'] === phieu['SOPHIEU']);
      setChiTietList(filteredDetails);

      // Tính toán lại tổng tiền dựa trên chi tiết và PT VAT của phiếu đang sửa
      const thanhTienTong = filteredDetails.reduce((sum, item) => sum + (parseFloat(item['THANHTIEN']) || 0), 0);
      const ptVat = parseFloat(phieu['PT VAT']) || 0;
      const tienVat = Math.round(thanhTienTong * (ptVat / 100));
      const tongTien = thanhTienTong + tienVat;

      setCurrentPhieu(prev => ({
        ...prev,
        'THANHTIEN': thanhTienTong,
        'TIEN VAT': tienVat,
        'TONGTIEN': tongTien
      }));
    } catch (error) {
      console.error('Error fetching chi tiet:', error);
      toast.error('Lỗi khi tải chi tiết phiếu');
      setChiTietList([]);
    }
  };

  const handleOpenDetailModal = async (phieu) => {
    // Format lại ngày để hiển thị đúng trong modal detail (nếu có input date)
    let formattedDate = '';
    if (phieu['NGAYNHAP_XUAT']) {
      try {
        const dateObj = new Date(phieu['NGAYNHAP_XUAT']);
        if (!isNaN(dateObj.getTime())) {
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        }
      } catch (e) {
        console.error('Error formatting date:', e);
      }
    }

    setCurrentPhieu({
      ...phieu,
      'NGAYNHAP_XUAT': formattedDate
    });
    setCustomerSearchTerm(phieu['NCC_KHACHHANG'] || '');

    // Set additional fields
    setKlXeVaHang(parseFloat(phieu['KL XE VA HANG']) || 0);
    setKlXe(parseFloat(phieu['KL XE']) || 0);
    setKlHang(parseFloat(phieu['KL HANG']) || 0);
    setSoKien(parseFloat(phieu['SO KIEN']) || 0);
    setSoKgTbKien(parseFloat(phieu['SO KG TB/KIEN']) || 0);

    setShowDetailModal(true);

    try {
      const response = await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET_DOMINO', 'Find', {
        "Where": `SOPHIEU = '${phieu['SOPHIEU']}'`
      });
      // Filter client-side
      const filteredDetails = (response || []).filter(item => item['SOPHIEU'] === phieu['SOPHIEU']);
      setChiTietList(filteredDetails);
    } catch (error) {
      console.error('Error fetching chi tiet:', error);
      toast.error('Lỗi khi tải chi tiết phiếu');
      setChiTietList([]);
    }
  };

  const handleDeletePhieu = (phieu) => {
    setPhieuToDelete(phieu);
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    if (!phieuToDelete) return;

    // Prevent multiple submissions
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      // 1. Tìm và xóa chi tiết
      const response = await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET_DOMINO', 'Find', {
        "Where": `SOPHIEU = '${phieuToDelete['SOPHIEU']}'`
      });

      const chiTietToDelete = (response || []).filter(item => item['SOPHIEU'] === phieuToDelete['SOPHIEU']);

      if (chiTietToDelete.length > 0) {
        const rowsToDelete = chiTietToDelete.map(item => ({ "ID_CT": item['ID_CT'] }));
        await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET_DOMINO', 'Delete', {
          "Rows": rowsToDelete
        });
      }

      // 2. Xóa header
      await authUtils.apiRequestKHO('XUATNHAPKHO_DOMINO', 'Delete', {
        "Rows": [{ 'SOPHIEU': phieuToDelete['SOPHIEU'] }]
      });

      toast.success('Xóa phiếu thành công!');
      await fetchPhieu();
      setShowDeleteConfirmation(false);
      setPhieuToDelete(null);
    } catch (error) {
      console.error('Error deleting phieu:', error);
      toast.error('Có lỗi xảy ra khi xóa phiếu');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validation trước khi lưu
  const validateBeforeSave = () => {
    // Validation cơ bản
    if (!currentPhieu['NCC_KHACHHANG']) {
      toast.error('Vui lòng chọn NCC/Khách hàng');
      return false;
    }

    if (currentPhieu['NGHIEP_VU'] === 'NHAP' && !currentPhieu['KHONHAP']) {
      toast.error('Vui lòng chọn kho nhập');
      return false;
    }

    if (currentPhieu['NGHIEP_VU'] === 'XUAT' && !currentPhieu['KHOXUAT']) {
      toast.error('Vui lòng chọn kho xuất');
      return false;
    }

    if (chiTietList.length === 0) {
      toast.error('Vui lòng thêm ít nhất một chi tiết');
      return false;
    }

    // Validation cho XUẤT KHO
    if (currentPhieu['NGHIEP_VU'] === 'XUAT') {
      if (!klXeVaHang || parseFloat(klXeVaHang) <= 0) {
        toast.error('Vui lòng nhập KL Xe và Hàng');
        return false;
      }
      if (!klXe || parseFloat(klXe) <= 0) {
        toast.error('Vui lòng nhập KL Xe');
        return false;
      }
      if (!soKien || parseFloat(soKien) <= 0) {
        toast.error('Vui lòng nhập Số kiện');
        return false;
      }

      // Kiểm tra tổng số kiện chi tiết = số kiện header
      const tongSoKienChiTiet = chiTietList.reduce((sum, item) => sum + (parseFloat(item['SO KIEN']) || 0), 0);
      const soKienHeader = parseFloat(soKien) || 0;

      if (Math.abs(tongSoKienChiTiet - soKienHeader) > 0.01) {
        toast.error(`❌ Tổng số kiện chi tiết (${formatNumber(tongSoKienChiTiet)}) không khớp với số kiện header (${formatNumber(soKienHeader)})`);
        return false;
      }

      // Kiểm tra tổng khối lượng chi tiết = KL hàng header
      const tongKhoiLuongChiTiet = chiTietList.reduce((sum, item) => sum + (parseFloat(item['TONG KG UL']) || 0), 0);
      const klHangHeader = parseFloat(klHang) || 0;

      const chenhLech = tongKhoiLuongChiTiet - klHangHeader;

      if (Math.abs(chenhLech) > 0.01) {
        if (chenhLech > 0) {
          // Vượt quá
          toast.error(`❌ Khối lượng chi tiết VƯỢT ${formatNumber(chenhLech)} kg so với KL hàng header!\n\nChi tiết: ${formatNumber(tongKhoiLuongChiTiet)} kg\nHeader: ${formatNumber(klHangHeader)} kg`, {
            autoClose: 5000
          });
        } else {
          // Thiếu
          toast.warning(`⚠️ Khối lượng chi tiết còn THIẾU ${formatNumber(Math.abs(chenhLech))} kg so với KL hàng header!\n\nChi tiết: ${formatNumber(tongKhoiLuongChiTiet)} kg\nHeader: ${formatNumber(klHangHeader)} kg`, {
            autoClose: 5000
          });
        }
        return false;
      }
    }

    return true;
  };

  const handleSubmitPhieu = async () => {
    if (isSubmitting) return;

    // Validation
    if (!validateBeforeSave()) {
      return;
    }

    setIsSubmitting(true);

    try {
      let soPhieu = currentPhieu['SOPHIEU'];

      // Tạo số phiếu mới nếu chưa có
      if (!isEditMode || !soPhieu) {
        soPhieu = generateSoPhieu(currentPhieu['NGHIEP_VU'], currentPhieu['NGAYNHAP_XUAT']);
      }

      // Tính tổng tiền từ chi tiết
      const thanhTienTong = chiTietList.reduce((sum, item) => sum + (parseFloat(item['THANHTIEN']) || 0), 0);
      const ptVat = parseFloat(currentPhieu['PT VAT']) || 0;
      const tienVat = Math.round(thanhTienTong * (ptVat / 100));
      const tongTien = thanhTienTong + tienVat;

      // Chuẩn bị dữ liệu header
      const headerData = {
        'SOPHIEU': soPhieu,
        'NGHIEP_VU': currentPhieu['NGHIEP_VU'],
        'NGAYNHAP_XUAT': currentPhieu['NGAYNHAP_XUAT'],
        'NCC_KHACHHANG': currentPhieu['NCC_KHACHHANG'],
        'KHOXUAT': currentPhieu['KHOXUAT'] || '',
        'KHONHAP': currentPhieu['KHONHAP'] || '',
        'NGUOIPHUTRACH': currentPhieu['NGUOIPHUTRACH'],
        'THANHTIEN': thanhTienTong,
        'PT VAT': ptVat,
        'TIEN VAT': tienVat,
        'TONGTIEN': tongTien,
        'DIENGIAI': currentPhieu['DIENGIAI'] || ''
      };

      // Thêm các trường đặc biệt cho XUẤT KHO
      if (currentPhieu['NGHIEP_VU'] === 'XUAT') {
        headerData['KL XE VA HANG'] = parseFloat(klXeVaHang) || 0;
        headerData['KL XE'] = parseFloat(klXe) || 0;
        headerData['KL HANG'] = parseFloat(klHang) || 0;
        headerData['SO KIEN'] = parseFloat(soKien) || 0;
        headerData['SO KG TB/KIEN'] = parseFloat(soKgTbKien) || 0;
      } else {
        // Cho NHẬP KHO, tính từ chi tiết
        const tongSoKien = chiTietList.reduce((sum, item) => sum + (parseFloat(item['SO KIEN']) || 0), 0);
        const tongKgUl = chiTietList.reduce((sum, item) => sum + (parseFloat(item['TONG KG UL']) || 0), 0);
        const soKgTbKienCalc = tongSoKien > 0 ? tongKgUl / tongSoKien : 0;

        headerData['KL XE VA HANG'] = 0;
        headerData['KL XE'] = 0;
        headerData['KL HANG'] = tongKgUl;
        headerData['SO KIEN'] = tongSoKien;
        headerData['SO KG TB/KIEN'] = soKgTbKienCalc;
      }

      // Lưu header
      if (isEditMode) {
        await authUtils.apiRequestKHO('XUATNHAPKHO_DOMINO', 'Edit', {
          "Rows": [headerData]
        });

        // Xóa chi tiết cũ: Phải tìm ID rồi xóa theo Rows (API không hỗ trợ Delete Where)
        const oldDetailsResponse = await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET_DOMINO', 'Find', {
          "Where": `SOPHIEU = '${originalSoPhieu}'`
        });

        const oldDetailsToDelete = (oldDetailsResponse || []).filter(item => item['SOPHIEU'] === originalSoPhieu);

        if (oldDetailsToDelete.length > 0) {
          const rowsToDelete = oldDetailsToDelete.map(item => ({ "ID_CT": item['ID_CT'] }));
          await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET_DOMINO', 'Delete', {
            "Rows": rowsToDelete
          });
        }
      } else {
        await authUtils.apiRequestKHO('XUATNHAPKHO_DOMINO', 'Add', {
          "Rows": [headerData]
        });
      }

      // Lưu chi tiết
      const chiTietData = chiTietList.map((item, index) => ({
        'ID_CT': Date.now() + index,
        'SOPHIEU': soPhieu,
        'NGHIEP_VU': currentPhieu['NGHIEP_VU'],
        'KHO_XUAT': currentPhieu['KHOXUAT'] || '',
        'KHO_NHAP': currentPhieu['KHONHAP'] || '',
        'NGAY_NHAP_XUAT': currentPhieu['NGAYNHAP_XUAT'],
        'NHOM_HANG': item['NHOM_HANG'],
        'TIEU_CHUAN': item['TIEU_CHUAN'] || '',
        'DOI_HANG_KHO': item['DOI_HANG_KHO'] || '',
        'SO KIEN': parseFloat(item['SO KIEN']) || 0,
        'SO KG TB/KIEN': parseFloat(item['SO KG TB/KIEN']) || 0,
        'SO KG TB/M3': parseFloat(item['SO KG TB/M3']) || 0,
        'TONG KG UL': parseFloat(item['TONG KG UL']) || 0,
        'TONG M3 UL': parseFloat(item['TONG M3 UL']) || 0,
        'DONGIA': parseFloat(item['DONGIA']) || 0,
        'THANHTIEN': parseFloat(item['THANHTIEN']) || 0,
        'GHICHU': item['GHICHU'] || ''
      }));

      await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET_DOMINO', 'Add', {
        "Rows": chiTietData
      });

      toast.success(isEditMode ? 'Cập nhật phiếu thành công!' : 'Thêm phiếu mới thành công!');
      setShowModal(false);
      await fetchPhieu();
    } catch (error) {
      console.error('Error submitting phieu:', error);
      toast.error('Có lỗi xảy ra khi lưu phiếu');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Thêm dòng trống vào bảng chi tiết
  const handleAddEmptyRow = () => {
    const emptyRow = {
      'NHOM_HANG': 'Domino - 18',
      'TIEU_CHUAN': '',
      'DOI_HANG_KHO': '',
      'SO KIEN': 0,
      'SO KG TB/KIEN': currentPhieu['NGHIEP_VU'] === 'NHAP' ? 450 : parseFloat(soKgTbKien) || 0,
      'SO KG TB/M3': 750,
      'TONG KG UL': 0,
      'TONG M3 UL': 0,
      'DONGIA': 0,
      'THANHTIEN': 0,
      'GHICHU': ''
    };
    const newList = [...chiTietList, emptyRow];
    setChiTietList(newList);
    updateTotals(newList);
  };

  // Cập nhật giá trị từng ô trong bảng chi tiết
  const handleUpdateChiTiet = (index, field, value) => {
    const updatedList = [...chiTietList];
    updatedList[index][field] = value;

    // Tự động tính toán lại các giá trị liên quan
    if (currentPhieu['NGHIEP_VU'] === 'NHAP') {
      const calculated = calculateNhapKho(updatedList[index]);
      updatedList[index] = { ...updatedList[index], ...calculated };
    } else {
      // XUẤT KHO: Sử dụng Số KG TB/kiện từ header
      const headerSoKgTbKien = parseFloat(soKgTbKien) || 0;
      const calculated = calculateXuatKho(updatedList[index], headerSoKgTbKien);
      updatedList[index] = { ...updatedList[index], ...calculated };
    }

    setChiTietList(updatedList);
    updateTotals(updatedList);
  };

  const handleDeleteChiTiet = (index) => {
    const newChiTietList = chiTietList.filter((_, i) => i !== index);
    setChiTietList(newChiTietList);
    updateTotals(newChiTietList);
    toast.success('Xóa chi tiết thành công!');
  };

  // Cập nhật KL HANG khi thay đổi KL XE VA HANG hoặc KL XE
  const handleKlXeVaHangChange = (value) => {
    const klXeVaHangVal = parseFloat(value) || 0;
    const klXeVal = parseFloat(klXe) || 0;
    const klHangVal = klXeVaHangVal - klXeVal;
    const soKienVal = parseFloat(soKien) || 0;
    const soKgTbKienVal = soKienVal > 0 ? klHangVal / soKienVal : 0;

    setKlXeVaHang(klXeVaHangVal);
    setKlHang(klHangVal);
    setSoKgTbKien(soKgTbKienVal);

    // Cập nhật lại tất cả chi tiết với Số KG TB/kiện mới
    updateAllChiTietWithNewKgTbKien(soKgTbKienVal);
  };

  const handleKlXeChange = (value) => {
    const klXeVal = parseFloat(value) || 0;
    const klXeVaHangVal = parseFloat(klXeVaHang) || 0;
    const klHangVal = klXeVaHangVal - klXeVal;
    const soKienVal = parseFloat(soKien) || 0;
    const soKgTbKienVal = soKienVal > 0 ? klHangVal / soKienVal : 0;

    setKlXe(klXeVal);
    setKlHang(klHangVal);
    setSoKgTbKien(soKgTbKienVal);

    // Cập nhật lại tất cả chi tiết với Số KG TB/kiện mới
    updateAllChiTietWithNewKgTbKien(soKgTbKienVal);
  };

  // Xử lý khi thay đổi Số kiện (cho XUẤT KHO)
  const handleSoKienChange = (value) => {
    const soKienVal = parseFloat(value) || 0;
    const klHangVal = parseFloat(klHang) || 0;
    const soKgTbKienVal = soKienVal > 0 ? klHangVal / soKienVal : 0;

    setSoKien(soKienVal);
    setSoKgTbKien(soKgTbKienVal);

    // Cập nhật lại tất cả chi tiết với Số KG TB/kiện mới
    updateAllChiTietWithNewKgTbKien(soKgTbKienVal);
  };

  // Cập nhật tất cả chi tiết khi Số KG TB/kiện header thay đổi (chỉ cho XUẤT KHO)
  const updateAllChiTietWithNewKgTbKien = (newSoKgTbKien) => {
    if (currentPhieu['NGHIEP_VU'] !== 'XUAT') return;

    const updatedList = chiTietList.map(item => {
      const calculated = calculateXuatKho(item, newSoKgTbKien);
      return { ...item, ...calculated };
    });

    setChiTietList(updatedList);
    updateTotals(updatedList);
  };

  // Thêm tiêu chuẩn mới
  const handleAddTieuChuan = async () => {
    if (!newTieuChuan.trim()) {
      toast.error('Vui lòng nhập tiêu chuẩn');
      return;
    }

    const exists = tieuChuanList.find(tc =>
      tc['TIEU_CHUAN'].toLowerCase() === newTieuChuan.trim().toLowerCase()
    );

    if (exists) {
      toast.error('Tiêu chuẩn này đã tồn tại');
      return;
    }

    try {
      const newTC = {
        'ID_TC': Date.now(),
        'TIEU_CHUAN': newTieuChuan.trim().toUpperCase()
      };

      await authUtils.apiRequestKHO('CD_TIEUCHUAN', 'Add', {
        "Rows": [newTC]
      });

      toast.success('Thêm tiêu chuẩn mới thành công!');
      await fetchTieuChuan();
      setNewTieuChuan('');
      setShowTieuChuanModal(false);
    } catch (error) {
      console.error('Error adding tieu chuan:', error);
      toast.error('Có lỗi xảy ra khi thêm tiêu chuẩn');
    }
  };

  const handleDeleteTieuChuan = async (idTC) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa tiêu chuẩn này?')) {
      return;
    }

    try {
      await authUtils.apiRequestKHO('CD_TIEUCHUAN', 'Delete', {
        "Rows": [{ "ID_TC": idTC }]
      });

      toast.success('Xóa tiêu chuẩn thành công!');
      await fetchTieuChuan();
    } catch (error) {
      console.error('Error deleting tieu chuan:', error);
      toast.error('Có lỗi xảy ra khi xóa tiêu chuẩn');
    }
  };

  // Thêm đội hàng khô mới
  const handleAddDoiHangKho = async () => {
    if (!newDoiHangKho.trim()) {
      toast.error('Vui lòng nhập tên đội hàng khô');
      return;
    }

    const exists = doiHangKhoList.find(dhk =>
      dhk['DOI_HANG_KHO'].toLowerCase() === newDoiHangKho.trim().toLowerCase()
    );

    if (exists) {
      toast.error('Đội hàng khô này đã tồn tại');
      return;
    }

    try {
      const newDHK = {
        'ID_DHK': Date.now(),
        'DOI_HANG_KHO': newDoiHangKho.trim().toUpperCase()
      };

      await authUtils.apiRequestKHO('CD_DOIHANGKHO', 'Add', {
        "Rows": [newDHK]
      });

      toast.success('Thêm đội hàng khô mới thành công!');
      await fetchDoiHangKho();
      setNewDoiHangKho('');
      setShowDoiHangKhoModal(false);
    } catch (error) {
      console.error('Error adding doi hang kho:', error);
      toast.error('Có lỗi xảy ra khi thêm đội hàng khô');
    }
  };

  const handleDeleteDoiHangKho = async (idDHK) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa đội hàng khô này?')) {
      return;
    }

    try {
      await authUtils.apiRequestKHO('CD_DOIHANGKHO', 'Delete', {
        "Rows": [{ "ID_DHK": idDHK }]
      });

      toast.success('Xóa đội hàng khô thành công!');
      await fetchDoiHangKho();
    } catch (error) {
      console.error('Error deleting doi hang kho:', error);
      toast.error('Có lỗi xảy ra khi xóa đội hàng khô');
    }
  };

  // ==================== EXPORT/IMPORT FUNCTIONS ====================

  const handleExportTemplate = () => {
    const templateDate = new Date(2025, 0, 30);

    const templateNhapData = [
      {
        'Ngày': templateDate,
        'NCC/Khách hàng': 'NCC MẪU',
        'Kho nhập': 'Kho Phú Giáo',
        'Người phụ trách': 'Nguyễn Văn A',
        'Nhóm hàng': 'Domino - 18',
        'Tiêu chuẩn': 'AB',
        'Đội hàng khô': 'Đội 1',
        'Số kiện': 100,
        'Số KG TB/Kiện': 450,
        'Số KG TB/M3': 750,
        'Đơn giá': 0,
        'Ghi chú': ''
      }
    ];

    const templateXuatData = [
      {
        'Ngày': templateDate,
        'NCC/Khách hàng': 'Công ty XYZ',
        'Kho xuất': 'Kho Phú Giáo',
        'Người phụ trách': 'Nguyễn Văn B',
        'KL Xe và Hàng': 50000,
        'KL Xe': 5000,
        'Số kiện': 100,
        'Nhóm hàng': 'Domino - 18',
        'Tiêu chuẩn': 'AB',
        'Số kiện chi tiết': 100,
        'Đơn giá': 1000,
        'Diễn giải': ''
      }
    ];

    const wb = XLSX.utils.book_new();

    const wsNhap = XLSX.utils.json_to_sheet(templateNhapData, { cellDates: true });
    wsNhap['!cols'] = [
      { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 15 },
      { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 15 },
      { wch: 15 }, { wch: 30 }
    ];

    const rangeNhap = XLSX.utils.decode_range(wsNhap['!ref']);
    for (let R = rangeNhap.s.r + 1; R <= rangeNhap.e.r; ++R) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: 0 });
      if (wsNhap[cellAddress]) {
        wsNhap[cellAddress].z = 'dd/mm/yyyy';
      }
    }

    XLSX.utils.book_append_sheet(wb, wsNhap, 'Mẫu Nhập Kho Domino');

    const wsXuat = XLSX.utils.json_to_sheet(templateXuatData, { cellDates: true });
    wsXuat['!cols'] = [
      { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 15 },
      { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 18 },
      { wch: 15 }, { wch: 30 }
    ];

    const rangeXuat = XLSX.utils.decode_range(wsXuat['!ref']);
    for (let R = rangeXuat.s.r + 1; R <= rangeXuat.e.r; ++R) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: 0 });
      if (wsXuat[cellAddress]) {
        wsXuat[cellAddress].z = 'dd/mm/yyyy';
      }
    }

    XLSX.utils.book_append_sheet(wb, wsXuat, 'Mẫu Xuất Kho Domino');

    const excelBuffer = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
      cellDates: true
    });

    const now = new Date();
    const day = now.getDate().toString().padStart(2, '0');
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const year = now.getFullYear();
    const fileName = `Mẫu nhập xuất kho Domino ngày ${day}-${month}-${year}.xlsx`;

    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, fileName);

    toast.success('Đã tải xuống file mẫu Excel Domino');
  };

  const handleExportData = () => {
    if (filteredAndSortedPhieuList.length === 0) {
      toast.error('Không có dữ liệu để xuất');
      return;
    }

    const exportData = filteredAndSortedPhieuList.map(phieu => ({
      'Số phiếu': phieu['SOPHIEU'],
      'Nghiệp vụ': phieu['NGHIEP_VU'],
      'Ngày': convertDateYYYYMMDDtoDDMMYYYY(phieu['NGAYNHAP_XUAT']),
      'NCC/Khách hàng': phieu['NCC_KHACHHANG'],
      'Kho xuất': phieu['KHOXUAT'],
      'Kho nhập': phieu['KHONHAP'],
      'Người phụ trách': phieu['NGUOIPHUTRACH'],
      'KL Xe và Hàng': formatNumber(phieu['KL XE VA HANG']),
      'KL Xe': formatNumber(phieu['KL XE']),
      'KL Hàng': formatNumber(phieu['KL HANG']),
      'Số kiện': formatNumber(phieu['SO KIEN']),
      'Số KG TB/Kiện': formatNumber(phieu['SO KG TB/KIEN']),
      'Tổng tiền': formatNumber(phieu['TONGTIEN']),
      'Diễn giải': phieu['DIENGIAI']
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    ws['!cols'] = [
      { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 25 }, { wch: 15 },
      { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 30 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Xuất Nhập Kho Domino');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const now = new Date();
    const day = now.getDate().toString().padStart(2, '0');
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const year = now.getFullYear();
    const fileName = `Xuất nhập kho Domino ${day}-${month}-${year}.xlsx`;

    saveAs(data, fileName);
    toast.success('Đã xuất dữ liệu thành công!');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Vui lòng chọn file Excel (.xlsx hoặc .xls)');
      return;
    }

    setImportFile(file);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;

        const wb = XLSX.read(bstr, {
          type: 'binary',
          cellDates: true,
          cellNF: false,
          cellText: false
        });

        let sheetName;
        if (importNghiepVu === 'NHAP') {
          sheetName = 'Mẫu Nhập Kho Domino';
        } else if (importNghiepVu === 'XUAT') {
          sheetName = 'Mẫu Xuất Kho Domino';
        } else {
          sheetName = wb.SheetNames[0];
        }

        if (!wb.Sheets[sheetName]) {
          toast.error(`Không tìm thấy sheet "${sheetName}" trong file Excel!`);
          console.error('Các sheet có sẵn:', wb.SheetNames);
          return;
        }

        console.log(`✅ Đang đọc sheet: "${sheetName}"`);

        const ws = wb.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(ws, {
          raw: false,
          dateNF: 'dd/mm/yyyy'
        });

        if (data.length === 0) {
          toast.error('File Excel không có dữ liệu');
          return;
        }

        console.log(`📊 Đọc được ${data.length} dòng từ sheet "${sheetName}"`);

        // Parse Excel Date
        const parseExcelDate = (dateValue) => {
          if (!dateValue) return new Date().toISOString().split('T')[0];

          if (dateValue instanceof Date) {
            return dateValue.toISOString().split('T')[0];
          }

          if (typeof dateValue === 'string' && dateValue.includes('/')) {
            const parts = dateValue.split('/');
            if (parts.length === 3) {
              const day = parts[0].padStart(2, '0');
              const month = parts[1].padStart(2, '0');
              const year = parts[2];
              return `${year}-${month}-${day}`;
            }
          }

          if (typeof dateValue === 'number') {
            const excelEpoch = new Date(1900, 0, 1);
            const days = dateValue - 2;
            const date = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);
            return date.toISOString().split('T')[0];
          }

          if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return dateValue;
          }

          return new Date().toISOString().split('T')[0];
        };

        const processedData = data.map((row, index) => {
          const ngayYYYYMMDD = parseExcelDate(row['Ngày']);
          const ngayDDMMYYYY = convertDateYYYYMMDDtoDDMMYYYY(ngayYYYYMMDD);

          let nccKhachHang = (row['NCC/Khách hàng'] || '').toString().trim();
          nccKhachHang = nccKhachHang.replace(/\s+/g, ' ');

          let processedRow = {
            rowIndex: index + 2,
            ngay: ngayDDMMYYYY,
            ngayYYYYMMDD: ngayYYYYMMDD,
            nccKhachHang: nccKhachHang,
            nguoiPhuTrach: (row['Người phụ trách'] || '').toString().trim(),
            dienGiai: (row['Diễn giải'] || row['Ghi chú'] || '').toString().trim(),
            nhomHang: (row['Nhóm hàng'] || 'Domino - 18').toString().trim(),
            tieuChuan: (row['Tiêu chuẩn'] || '').toString().trim(),
            errors: []
          };

          // ✅ VALIDATE NCC/KHÁCH HÀNG (PHẢI CÓ TRONG DATA)
          if (!nccKhachHang) {
            processedRow.errors.push('Thiếu NCC/Khách hàng');
          } else {
            const customerMatch = customers.find(c =>
              c['TEN_KHACHHANG']?.trim().toLowerCase() === nccKhachHang.toLowerCase()
            );

            if (!customerMatch) {
              processedRow.errors.push(`NCC/Khách hàng "${nccKhachHang}" không tồn tại trong hệ thống`);
            } else {
              // Chuẩn hóa tên theo database
              processedRow.nccKhachHang = customerMatch['TEN_KHACHHANG'];

              // Nếu là Xuất Kho thì phải là KH (theo yêu cầu lọc)
              /* 
                 Tạm thời chỉ bắt buộc tồn tại trong hệ thống. 
                 Nếu cần bắt buộc loại KH cho phiếu xuất thì uncomment dòng dưới:
                 if (importNghiepVu === 'XUAT' && customerMatch['PHAN_LOAI'] !== 'KH') {
                   processedRow.errors.push(`"${nccKhachHang}" không phải là Khách hàng (loại KH)`);
                 }
              */
            }
          }

          if (importNghiepVu === 'NHAP') {
            // NHẬP KHO
            const kho = (row['Kho nhập'] || '').toString().trim();
            const doiHangKho = (row['Đội hàng khô'] || '').toString().trim();
            const soKien = parseFloat(row['Số kiện']) || 0;
            const soKgTbKien = parseFloat(row['Số KG TB/Kiện']) || 450;
            const soKgTbM3 = parseFloat(row['Số KG TB/M3']) || 750;
            const donGia = parseFloat(row['Đơn giá']) || 0;

            const tongKgUl = soKien * soKgTbKien;
            const tongM3Ul = tongKgUl / soKgTbM3;
            const thanhTien = tongKgUl * donGia;

            processedRow = {
              ...processedRow,
              kho: kho,
              doiHangKho: doiHangKho,
              soKien: soKien,
              soKgTbKien: soKgTbKien,
              soKgTbM3: soKgTbM3,
              tongKgUl: tongKgUl,
              tongM3Ul: tongM3Ul,
              donGia: donGia,
              thanhTien: thanhTien
            };

            // Validation
            if (!kho) processedRow.errors.push('Thiếu kho nhập');
            if (soKien <= 0) processedRow.errors.push('Số kiện phải > 0');

          } else {
            // XUẤT KHO
            const kho = (row['Kho xuất'] || '').toString().trim();
            const klXeVaHang = parseFloat(row['KL Xe và Hàng']) || 0;
            const klXe = parseFloat(row['KL Xe']) || 0;
            const soKienHeader = parseFloat(row['Số kiện']) || 0;
            const soKienChiTiet = parseFloat(row['Số kiện chi tiết']) || 0;
            const donGia = parseFloat(row['Đơn giá']) || 0;

            const klHang = klXeVaHang - klXe;
            const soKgTbKien = soKienHeader > 0 ? klHang / soKienHeader : 0;
            const tongKgUl = soKienChiTiet * soKgTbKien;
            const thanhTien = tongKgUl * donGia;

            processedRow = {
              ...processedRow,
              kho: kho,
              klXeVaHang: klXeVaHang,
              klXe: klXe,
              klHang: klHang,
              soKienHeader: soKienHeader,
              soKienChiTiet: soKienChiTiet,
              soKgTbKien: soKgTbKien,
              tongKgUl: tongKgUl,
              donGia: donGia,
              thanhTien: thanhTien
            };

            // Validation
            if (!kho) processedRow.errors.push('Thiếu kho xuất');
            if (klXeVaHang <= 0) processedRow.errors.push('KL Xe và Hàng phải > 0');
            if (klXe <= 0) processedRow.errors.push('KL Xe phải > 0');
            if (soKienHeader <= 0) processedRow.errors.push('Số kiện phải > 0');
            if (soKienChiTiet <= 0) processedRow.errors.push('Số kiện chi tiết phải > 0');
          }

          // Common validation
          if (!ngayYYYYMMDD) processedRow.errors.push('Thiếu ngày');
          if (!nccKhachHang) processedRow.errors.push('Thiếu NCC/Khách hàng');

          return processedRow;
        });

        setImportPreview(processedData);

        const validCount = processedData.filter(r => r.errors.length === 0).length;
        const errorCount = processedData.filter(r => r.errors.length > 0).length;

        if (validCount > 0) {
          toast.success(`Đã đọc ${processedData.length} dòng từ sheet "${sheetName}": ${validCount} hợp lệ, ${errorCount} lỗi`);
        } else {
          toast.error('Không có dòng dữ liệu hợp lệ');
        }
      } catch (error) {
        console.error('❌ Error reading Excel file:', error);
        toast.error('Có lỗi khi đọc file Excel: ' + error.message);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleImportData = async () => {
    if (importPreview.length === 0) {
      toast.error('Không có dữ liệu để import');
      return;
    }

    const validRows = importPreview.filter(row => row.errors.length === 0);
    if (validRows.length === 0) {
      toast.error('Không có dòng dữ liệu hợp lệ để import');
      return;
    }

    const hasErrors = importPreview.some(row => row.errors.length > 0);
    if (hasErrors) {
      toast.error('Vui lòng sửa tất cả các lỗi trước khi import');
      return;
    }

    try {
      setIsImporting(true);

      // Group data theo phiếu
      const groupedData = {};

      validRows.forEach(row => {
        const key = `${row.ngayYYYYMMDD}_${row.nccKhachHang}_${row.kho}_${row.nguoiPhuTrach}`;

        if (!groupedData[key]) {
          groupedData[key] = {
            ngay: row.ngayYYYYMMDD,
            kho: row.kho,
            nccKhachHang: row.nccKhachHang,
            nguoiPhuTrach: row.nguoiPhuTrach,
            dienGiai: row.dienGiai,
            chiTiet: []
          };

          // Thêm thông tin đặc biệt cho XUẤT KHO
          if (importNghiepVu === 'XUAT') {
            groupedData[key].klXeVaHang = row.klXeVaHang;
            groupedData[key].klXe = row.klXe;
            groupedData[key].klHang = row.klHang;
            groupedData[key].soKienHeader = row.soKienHeader;
            groupedData[key].soKgTbKien = row.soKgTbKien;
          }
        }

        groupedData[key].chiTiet.push(row);
      });

      let successCount = 0;
      let errorCount = 0;

      console.log(`📊 Tổng số phiếu sẽ tạo: ${Object.keys(groupedData).length}`);

      for (const key of Object.keys(groupedData)) {
        const group = groupedData[key];

        try {
          // Tạo số phiếu
          const soPhieu = generateSoPhieu(importNghiepVu, group.ngay);

          // Tính tổng
          const tongTien = group.chiTiet.reduce((sum, item) => sum + (item.thanhTien || 0), 0);

          // Chuẩn bị header
          const headerData = {
            'SOPHIEU': soPhieu,
            'NGHIEP_VU': importNghiepVu,
            'NGAYNHAP_XUAT': group.ngay,
            'NCC_KHACHHANG': group.nccKhachHang,
            'KHOXUAT': importNghiepVu === 'XUAT' ? group.kho : '',
            'KHONHAP': importNghiepVu === 'NHAP' ? group.kho : '',
            'NGUOIPHUTRACH': group.nguoiPhuTrach,
            'THANHTIEN': tongTien,
            'PT VAT': 0,
            'TIEN VAT': 0,
            'TONGTIEN': tongTien,
            'DIENGIAI': group.dienGiai || ''
          };

          if (importNghiepVu === 'XUAT') {
            headerData['KL XE VA HANG'] = group.klXeVaHang;
            headerData['KL XE'] = group.klXe;
            headerData['KL HANG'] = group.klHang;
            headerData['SO KIEN'] = group.soKienHeader;
            headerData['SO KG TB/KIEN'] = group.soKgTbKien;
          } else {
            const tongSoKien = group.chiTiet.reduce((sum, item) => sum + (item.soKien || 0), 0);
            const tongKgUl = group.chiTiet.reduce((sum, item) => sum + (item.tongKgUl || 0), 0);
            const soKgTbKienCalc = tongSoKien > 0 ? tongKgUl / tongSoKien : 0;

            headerData['KL XE VA HANG'] = 0;
            headerData['KL XE'] = 0;
            headerData['KL HANG'] = tongKgUl;
            headerData['SO KIEN'] = tongSoKien;
            headerData['SO KG TB/KIEN'] = soKgTbKienCalc;
          }

          // Lưu header
          await authUtils.apiRequestKHO('XUATNHAPKHO_DOMINO', 'Add', {
            "Rows": [headerData]
          });

          // Lưu chi tiết
          const chiTietData = group.chiTiet.map((item, index) => ({
            'ID_CT': Date.now() + index,
            'SOPHIEU': soPhieu,
            'NGHIEP_VU': importNghiepVu,
            'KHO_XUAT': importNghiepVu === 'XUAT' ? group.kho : '',
            'KHO_NHAP': importNghiepVu === 'NHAP' ? group.kho : '',
            'NGAY_NHAP_XUAT': group.ngay,
            'NHOM_HANG': item.nhomHang,
            'TIEU_CHUAN': item.tieuChuan || '',
            'DOI_HANG_KHO': item.doiHangKho || '',
            'SO KIEN': importNghiepVu === 'NHAP' ? item.soKien : item.soKienChiTiet,
            'SO KG TB/KIEN': item.soKgTbKien || 0,
            'SO KG TB/M3': item.soKgTbM3 || 0,
            'TONG KG UL': item.tongKgUl || 0,
            'TONG M3 UL': item.tongM3Ul || 0,
            'DONGIA': item.donGia || 0,
            'THANHTIEN': item.thanhTien || 0,
            'GHICHU': ''
          }));

          await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET_DOMINO', 'Add', {
            "Rows": chiTietData
          });

          console.log(`✅ Đã tạo phiếu ${soPhieu}: ${group.nccKhachHang} - ${group.chiTiet.length} dòng`);
          successCount++;

          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          console.error(`❌ Error importing phieu:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Import thành công ${successCount} phiếu với ${validRows.length} dòng`);
        await fetchPhieu();
        handleCloseImportModal();
      }

      if (errorCount > 0) {
        toast.warning(`Có ${errorCount} phiếu import thất bại`);
      }

    } catch (error) {
      console.error('Error importing data:', error);
      toast.error('Có lỗi xảy ra khi import dữ liệu: ' + error.message);
    } finally {
      setIsImporting(false);
    }
  };

  const handleOpenImportModal = () => {
    setShowImportModal(true);
    setImportFile(null);
    setImportPreview([]);
    setImportNghiepVu('NHAP');
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportPreview([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ==================== PRINT FUNCTIONS ====================

  const handleOpenPrintOptions = (phieu) => {
    if (phieu['NGHIEP_VU'] === 'XUAT') {
      setPhieuToPrint(phieu);
      setShowPrintOptions(true);
      setPrintWithPrice(false);
    } else {
      handlePrintPhieu(phieu, false);
    }
  };

  const handlePrintPhieu = async (phieu, showPrice = false, companyCode = 'HOANG_NGUYEN_LONG') => {
    try {
      toast.info('Đang tải dữ liệu để in...');

      const response = await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET_DOMINO', 'Find', {
        "Where": `SOPHIEU = '${phieu['SOPHIEU']}'`
      });
      // Filter client-side
      const chiTiet = (response || []).filter(item => item['SOPHIEU'] === phieu['SOPHIEU']);

      if (chiTiet.length === 0) {
        toast.error('Không tìm thấy chi tiết phiếu để in');
        return;
      }

      const date = new Date(phieu['NGAYNHAP_XUAT']);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      const isNhapKho = phieu['NGHIEP_VU'] === 'NHAP';
      const isXuatKho = phieu['NGHIEP_VU'] === 'XUAT';

      // Thông tin công ty
      const companyInfo = companyCode === 'HOANG_KHOI'
        ? {
          name: 'CÔNG TY TNHH GỖ HOÀNG KHÔI',
          mst: 'MST: 3702896657',
          phone: ''
        }
        : {
          name: 'CÔNG TY CP TM SX XD HOÀNG NGUYÊN LONG',
          mst: '',
          phone: 'Điện thoại: 0919.509.009 - 0985.96.97.98 ( A. Lưỡng )'
        };

      const showPriceColumns = isXuatKho && showPrice;

      // Template in cho NHẬP KHO
      if (isNhapKho) {
        const printContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Phiếu Nhập Kho Domino - ${phieu['SOPHIEU']}</title>
  <style>
    @page { size: A4; margin: 10mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; line-height: 1.3; color: #000; }
    .container { width: 100%; max-width: 210mm; margin: 0 auto; padding: 5mm; }
    .header { text-align: center; margin-bottom: 10px; }
    .header h1 { font-size: 18pt; font-weight: bold; margin-bottom: 3px; text-transform: uppercase; }
    .header .date { font-weight: bold; font-size: 13pt; font-style: italic; margin-bottom: 5px; }
    .info-section { display: grid; grid-template-columns: 1fr 1fr; gap: 5px; margin-bottom: 10px; }
    .info-item { font-size: 11pt;}
    .info-item strong { font-weight: bold; min-width: 120px; display: inline-block; }
    table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 10pt; }
    table th { background-color: #f0f0f0; border: 1px solid #000; padding: 6px 4px; text-align: center; font-weight: bold; }
    table td { border: 1px solid #000; padding: 5px 4px; text-align: center; }
    table td.left { text-align: left; padding-left: 10px; }
    table td.right { text-align: right; padding-right: 10px; }
    .total-row { background-color: #ffff26; font-weight: bold; color: red; }
    .signatures { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; text-align: center; margin-top: 20px; }
    .signature-box { padding: 10px; }
    .signature-box .title { font-weight: bold; font-size: 11pt; }
    @media print {
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="company-name" style="font-size: 13pt; font-weight: bold;">${companyInfo.name}</div>
      ${companyInfo.mst ? `<div style="font-size: 9pt; margin-bottom: 5px;">${companyInfo.mst}</div>` : ''}
      ${companyInfo.phone ? `<div style="font-size: 9pt; margin-bottom: 10px;">${companyInfo.phone}</div>` : ''}
      <h1>PHIẾU NHẬP KHO DOMINO - 18</h1>
      <div class="date">Ngày ${day} Tháng ${month} Năm ${year}</div>
    </div>
    
    <div class="info-section">
      <div class="info-item"><strong>Số phiếu:</strong> ${phieu['SOPHIEU']}</div>
      <div class="info-item"><strong>Kho nhập:</strong> ${phieu['KHONHAP']}</div>
    </div>
    <div class="info-item"><strong>Nhà cung cấp:</strong> ${phieu['NCC_KHACHHANG'] || ''}</div>
    <div class="info-item"><strong>Người phụ trách:</strong> ${phieu['NGUOIPHUTRACH'] || ''}</div>
    
    <table>
      <thead>
        <tr>
          <th style="width: 30px;">STT</th>
          <th style="width: 120px;">Nhóm hàng</th>
          <th style="width: 80px;">Tiêu chuẩn</th>
          <th style="width: 100px;">Đội hàng khô</th>
          <th style="width: 70px;">Số kiện</th>
          <th style="width: 80px;">KG TB/Kiện</th>
          <th style="width: 80px;">KG TB/M³</th>
          <th style="width: 80px;">Tổng KG UL</th>
          <th style="width: 80px;">Tổng M³ UL</th>
        </tr>
      </thead>
      <tbody>
        ${chiTiet.map((item, index) => `
          <tr>
            <td>${index + 1}</td>
            <td class="left">${item['NHOM_HANG']}</td>
            <td>${item['TIEU_CHUAN'] || ''}</td>
            <td>${item['DOI_HANG_KHO'] || ''}</td>
            <td>${formatNumber(item['SO KIEN'])}</td>
            <td class="right">${formatNumber(item['SO KG TB/KIEN'])}</td>
            <td class="right">${formatNumber(item['SO KG TB/M3'])}</td>
            <td class="right">${formatNumber(item['TONG KG UL'])}</td>
            <td class="right">${formatNumber(item['TONG M3 UL'])}</td>
          </tr>
        `).join('')}
        
        <tr class="total-row">
          <td colspan="4" class="left"><strong>TỔNG CỘNG</strong></td>
          <td><strong>${formatNumber(chiTiet.reduce((sum, item) => sum + (parseFloat(item['SO KIEN']) || 0), 0))}</strong></td>
          <td></td>
          <td></td>
          <td class="right"><strong>${formatNumber(chiTiet.reduce((sum, item) => sum + (parseFloat(item['TONG KG UL']) || 0), 0))}</strong></td>
          <td class="right"><strong>${formatNumber(chiTiet.reduce((sum, item) => sum + (parseFloat(item['TONG M3 UL']) || 0), 0))}</strong></td>
        </tr>
      </tbody>
    </table>
    
    <div class="signatures">
      <div class="signature-box">
        <div class="title">Người lập phiếu</div>
        <div class="name">${phieu['NGUOIPHUTRACH'] || '(Ký và ghi rõ họ tên)'}</div>
      </div>
      <div class="signature-box">
        <div class="title">Thủ kho</div>
        <div class="name">(Ký và ghi rõ họ tên)</div>
      </div>
      <div class="signature-box">
        <div class="title">Kế toán trưởng</div>
        <div class="name">(Ký và ghi rõ họ tên)</div>
      </div>
    </div>
  </div>
  
  <script>
    window.onload = function() {
      window.print();
    }
  </script>
</body>
</html>
`;

        const printWindow = window.open('', '_blank', 'width=800,height=600');
        printWindow.document.write(printContent);
        printWindow.document.close();
      }

      // Template in cho XUẤT KHO
      if (isXuatKho) {
        const printContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Phiếu Xuất Kho Domino - ${phieu['SOPHIEU']}</title>
  <style>
    @page { size: A4; margin: 10mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Times New Roman', Times, serif; font-size: 11pt; line-height: 1.3; color: #000; }
    .container { width: 100%; max-width: 210mm; margin: 0 auto; padding: 8mm; }
    .header { text-align: center; margin-bottom: 10px; }
    .header .company-name { font-size: 13pt; font-weight: bold; }
    .header .title { font-size: 15pt; font-weight: bold; margin-bottom: 8px; text-transform: uppercase; }
    .date-number-section { display: flex; justify-content: flex-end; gap: 30px; margin-bottom: 5px; font-size: 11pt; }
    .date-number-section .item { display: flex; align-items: center; gap: 10px; }
    .date-number-section .value { border: 1px solid #000; padding: 3px 15px; min-width: 100px; text-align: center; }
    .info-section { margin-bottom: 10px; font-size: 11pt; }
    .info-row { display: flex; margin-bottom: 3px; }
    .info-label { font-weight: bold; min-width: 150px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 10pt; }
    table th { background-color: #f0f0f0; border: 1px solid #000; padding: 6px 4px; text-align: center; font-weight: bold; }
    table td { border: 1px solid #000; padding: 5px 4px; text-align: center; }
    table td.left { text-align: left; padding-left: 8px; }
    table td.right { text-align: right; padding-right: 8px; }
    .total-row { background-color: #ffeb3b; font-weight: bold; color: red; }
    .footer { display: flex; justify-content: space-between; font-size: 11pt; margin-top: 20px; }
    .footer-box { text-align: center; padding: 10px; }
    .footer-box .title { font-weight: bold; margin-bottom: 60px; }
    @media print {
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="company-name">${companyInfo.name}</div>
      ${companyInfo.mst ? `<div class="company-name">${companyInfo.mst}</div>` : ''}
      ${companyInfo.phone ? `<div class="company-name">${companyInfo.phone}</div>` : ''}
      <div class="title">PHIẾU XUẤT KHO DOMINO - 18</div>
    </div>
    
    <div class="date-number-section">
      <div class="item">
        <span class="label">Ngày</span>
        <span class="value">${day}/${month}/${year}</span>
      </div>
      <div class="item">
        <span class="label">Số phiếu:</span>
        <span class="value">${phieu['SOPHIEU']}</span>
      </div>
    </div>
    
    <div class="info-section">
      <div class="info-row">
        <span class="info-label">Khách hàng:</span>
        <span><strong>${phieu['NCC_KHACHHANG'] || ''}</strong></span>
      </div>
      <div class="info-row">
        <span class="info-label">KL Xe và Hàng:</span>
        <span><strong>${formatNumber(phieu['KL XE VA HANG'])} kg</strong></span>
      </div>
      <div class="info-row">
        <span class="info-label">KL Xe:</span>
        <span><strong>${formatNumber(phieu['KL XE'])} kg</strong></span>
      </div>
      <div class="info-row">
        <span class="info-label">KL Hàng:</span>
        <span><strong>${formatNumber(phieu['KL HANG'])} kg</strong></span>
      </div>
      <div class="info-row">
        <span class="info-label">Số kiện:</span>
        <span><strong>${formatNumber(phieu['SO KIEN'])}</strong></span>
      </div>
      <div class="info-row">
        <span class="info-label">Diễn giải:</span>
        <span><strong>${phieu['DIENGIAI'] || ''}</strong></span>
      </div>
    </div>
    
    <table>
      <thead>
        <tr>
          <th style="width: 30px;">STT</th>
          <th style="width: 120px;">Nhóm hàng</th>
          <th style="width: 80px;">Tiêu chuẩn</th>
          <th style="width: 70px;">Số kiện</th>
          <th style="width: 80px;">Khối lượng (kg)</th>
          ${showPriceColumns ? '<th style="width: 90px;">Đơn giá</th>' : ''}
          ${showPriceColumns ? '<th style="width: 100px;">Thành tiền</th>' : '<th style="width: 100px;">Ghi chú</th>'}
        </tr>
      </thead>
      <tbody>
        ${chiTiet.map((item, index) => `
          <tr>
            <td>${index + 1}</td>
            <td class="left">${item['NHOM_HANG']}</td>
            <td>${item['TIEU_CHUAN'] || ''}</td>
            <td>${formatNumber(item['SO KIEN'])}</td>
            <td class="right">${formatNumber(item['TONG KG UL'])}</td>
            ${showPriceColumns ? `<td class="right">${formatNumber(item['DONGIA'])}</td>` : ''}
            ${showPriceColumns ? `<td class="right">${formatNumber(item['THANHTIEN'])}</td>` : `<td>${item['GHICHU'] || ''}</td>`}
          </tr>
        `).join('')}
        
        <tr class="total-row">
          <td colspan="3" class="left"><strong>TỔNG CỘNG</strong></td>
          <td><strong>${formatNumber(chiTiet.reduce((sum, item) => sum + (parseFloat(item['SO KIEN']) || 0), 0))}</strong></td>
          <td class="right"><strong>${formatNumber(chiTiet.reduce((sum, item) => sum + (parseFloat(item['TONG KG UL']) || 0), 0))}</strong></td>
          ${showPriceColumns ? '<td></td>' : ''}
          ${showPriceColumns ? `<td class="right"><strong>${formatNumber(phieu['TONGTIEN'])}</strong></td>` : '<td></td>'}
        </tr>
      </tbody>
    </table>
    
    <div class="footer">
      <div class="footer-box">
        <div class="title">Bên nhận hàng</div>
      </div>
      <div class="footer-box">
        <div class="title">${companyInfo.name}</div>
      </div>
    </div>
  </div>
  
  <script>
    window.onload = function() {
      window.print();
    }
  </script>
</body>
</html>
`;

        const printWindow = window.open('', '_blank', 'width=800,height=600');
        printWindow.document.write(printContent);
        printWindow.document.close();
      }

      toast.dismiss();
      setShowPrintOptions(false);

    } catch (error) {
      console.error('Error printing phieu:', error);
      toast.error('Có lỗi xảy ra khi in phiếu: ' + error.message);
    }
  };

  // ==================== UTILITY FUNCTIONS ====================

  const formatNumber = (num) => {
    if (!num) return '0';
    return parseFloat(num).toLocaleString('vi-VN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const convertDateYYYYMMDDtoDDMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // ==================== CUSTOMER AUTOCOMPLETE ====================

  const filteredCustomers = React.useMemo(() => {
    return customers.filter(c => {
      // 1. Filter by search term
      const matchesSearch = !customerSearchTerm ||
        c['TEN_KHACHHANG']?.toLowerCase().includes(customerSearchTerm.toLowerCase());

      // 2. Filter by NGHIEP_VU (Export -> only KH)
      if (currentPhieu['NGHIEP_VU'] === 'XUAT') {
        return matchesSearch && c['PHAN_LOAI'] === 'KH';
      }

      return matchesSearch;
    });
  }, [customers, customerSearchTerm, currentPhieu['NGHIEP_VU']]);

  const handleCustomerSelect = (customer) => {
    setCurrentPhieu(prev => ({ ...prev, 'NCC_KHACHHANG': customer['TEN_KHACHHANG'] }));
    setCustomerSearchTerm(customer['TEN_KHACHHANG']);
    setShowCustomerDropdown(false);
  };

  const handleCustomerInputChange = (value) => {
    setCustomerSearchTerm(value);
    setCurrentPhieu(prev => ({
      ...prev,
      'NCC_KHACHHANG': value
    }));
    setShowCustomerDropdown(true);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ==================== FILTER & SORT ====================

  const filteredAndSortedPhieuList = React.useMemo(() => {
    let filtered = [...phieuList];

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(phieu =>
        phieu['SOPHIEU']?.toLowerCase().includes(searchLower) ||
        phieu['NCC_KHACHHANG']?.toLowerCase().includes(searchLower) ||
        phieu['NGUOIPHUTRACH']?.toLowerCase().includes(searchLower)
      );
    }

    // Nghiệp vụ filter
    if (filterNghiepVu !== 'ALL') {
      filtered = filtered.filter(phieu => phieu['NGHIEP_VU'] === filterNghiepVu);
    }

    // Customer filter
    if (filterCustomer !== 'ALL') {
      filtered = filtered.filter(phieu => phieu['NCC_KHACHHANG'] === filterCustomer);
    }

    // Date range filter
    if (filterDateFrom || filterDateTo) {
      filtered = filtered.filter(phieu => {
        // Convert sang Local Time để đảm bảo đúng ngày (tránh lỗi UTC lùi 1 ngày)
        let phieuDateStr = '';
        if (phieu['NGAYNHAP_XUAT']) {
          try {
            const dateObj = new Date(phieu['NGAYNHAP_XUAT']);
            if (!isNaN(dateObj.getTime())) {
              const year = dateObj.getFullYear();
              const month = String(dateObj.getMonth() + 1).padStart(2, '0');
              const day = String(dateObj.getDate()).padStart(2, '0');
              phieuDateStr = `${year}-${month}-${day}`;
            }
          } catch (e) {
            console.error('Error parsing date:', e);
          }
        }

        if (filterDateFrom && phieuDateStr) {
          if (phieuDateStr < filterDateFrom) return false;
        }
        if (filterDateTo && phieuDateStr) {
          if (phieuDateStr > filterDateTo) return false;
        }
        return true;
      });
    }

    // Sort
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();

        if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    } else {
      // Mặc định sắp xếp theo ngày mới nhất giảm dần
      filtered.sort((a, b) => {
        if (a['NGAYNHAP_XUAT'] < b['NGAYNHAP_XUAT']) return 1;
        if (a['NGAYNHAP_XUAT'] > b['NGAYNHAP_XUAT']) return -1;
        // Nếu cùng ngày, sắp xếp theo số phiếu giảm dần
        if (a['SOPHIEU'] < b['SOPHIEU']) return 1;
        if (a['SOPHIEU'] > b['SOPHIEU']) return -1;
        return 0;
      });
    }

    return filtered;
  }, [phieuList, search, filterNghiepVu, filterCustomer, filterDateFrom, filterDateTo, sortConfig]);

  // ==================== PAGINATION ====================

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <span className="text-gray-400 ml-1">⇅</span>;
    }
    return sortConfig.direction === 'ascending'
      ? <span className="text-purple-600 ml-1">↑</span>
      : <span className="text-purple-600 ml-1">↓</span>;
  };

  const filteredPhieuList = filteredAndSortedPhieuList;
  const totalPages = Math.ceil(filteredPhieuList.length / itemsPerPage);
  const currentItems = filteredPhieuList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...', totalPages);
      }
    }
    return pages;
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToPrevPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPage = (page) => setCurrentPage(page);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterNghiepVu, filterCustomer, filterDateFrom, filterDateTo, itemsPerPage]);

  // ==================== STATISTICS ====================

  const statistics = React.useMemo(() => {
    const totalPhieu = phieuList.length;
    const totalNhap = phieuList.filter(p => p['NGHIEP_VU'] === 'NHAP').length;
    const totalXuat = phieuList.filter(p => p['NGHIEP_VU'] === 'XUAT').length;
    const totalKgNhap = phieuList
      .filter(p => p['NGHIEP_VU'] === 'NHAP')
      .reduce((sum, p) => sum + (parseFloat(p['KL HANG']) || 0), 0);
    const totalKgXuat = phieuList
      .filter(p => p['NGHIEP_VU'] === 'XUAT')
      .reduce((sum, p) => sum + (parseFloat(p['KL HANG']) || 0), 0);
    const totalRevenue = phieuList
      .filter(p => p['NGHIEP_VU'] === 'XUAT')
      .reduce((sum, p) => sum + (parseFloat(p['TONGTIEN']) || 0), 0);

    return {
      totalPhieu,
      totalNhap,
      totalXuat,
      totalKgNhap,
      totalKgXuat,
      totalRevenue,
      tonKho: totalKgNhap - totalKgXuat
    };
  }, [phieuList]);

  const filteredStats = React.useMemo(() => {
    const totalNhap = filteredPhieuList.filter(p => p['NGHIEP_VU'] === 'NHAP').length;
    const totalXuat = filteredPhieuList.filter(p => p['NGHIEP_VU'] === 'XUAT').length;
    const totalKgNhap = filteredPhieuList
      .filter(p => p['NGHIEP_VU'] === 'NHAP')
      .reduce((sum, p) => sum + (parseFloat(p['KL HANG']) || 0), 0);
    const totalKgXuat = filteredPhieuList
      .filter(p => p['NGHIEP_VU'] === 'XUAT')
      .reduce((sum, p) => sum + (parseFloat(p['KL HANG']) || 0), 0);
    const totalKienNhap = filteredPhieuList
      .filter(p => p['NGHIEP_VU'] === 'NHAP')
      .reduce((sum, p) => sum + (parseFloat(p['SO KIEN']) || 0), 0);
    const totalKienXuat = filteredPhieuList
      .filter(p => p['NGHIEP_VU'] === 'XUAT')
      .reduce((sum, p) => sum + (parseFloat(p['SO KIEN']) || 0), 0);
    const totalRevenue = filteredPhieuList
      .filter(p => p['NGHIEP_VU'] === 'XUAT')
      .reduce((sum, p) => sum + (parseFloat(p['TONGTIEN']) || 0), 0);

    return {
      totalNhap,
      totalXuat,
      totalKgNhap,
      totalKgXuat,
      totalKienNhap,
      totalKienXuat,
      totalRevenue
    };
  }, [filteredPhieuList]);

  const uniqueCustomers = [...new Set(phieuList.map(p => p['NCC_KHACHHANG']).filter(Boolean))];

  // Tính tổng từ chi tiết để hiển thị cảnh báo
  const chiTietTotals = React.useMemo(() => {
    const tongSoKien = chiTietList.reduce((sum, item) => sum + (parseFloat(item['SO KIEN']) || 0), 0);
    const tongKhoiLuong = chiTietList.reduce((sum, item) => sum + (parseFloat(item['TONG KG UL']) || 0), 0);

    // Tính chênh lệch
    const soKienHeader = parseFloat(soKien) || 0;
    const klHangHeader = parseFloat(klHang) || 0;

    const chenhLechSoKien = tongSoKien - soKienHeader;
    const chenhLechKhoiLuong = tongKhoiLuong - klHangHeader;

    return {
      tongSoKien,
      tongKhoiLuong,
      chenhLechSoKien,
      chenhLechKhoiLuong
    };
  }, [chiTietList, soKien, klHang]);

  // ==================== RENDER ====================

  return (
    <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-3 md:p-4 mb-4 border border-gray-100">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-3 gap-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-800">Xuất Nhập Kho Domino - 18</h1>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5">Quản lý phiếu nhập xuất kho Domino - 18</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm ${showFilters
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Bộ lọc</span>
              </button>
              <button
                onClick={() => setShowTieuChuanModal(true)}
                className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
              >
                <Info className="w-4 h-4" />
                <span className="hidden sm:inline">Tiêu chuẩn</span>
              </button>
              <button
                onClick={() => setShowDoiHangKhoModal(true)}
                className="px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
              >
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Đội hàng khô</span>
              </button>
              <button
                onClick={handleOpenImportModal}
                className="px-3 py-1.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
                title="Import từ Excel"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Import Excel</span>
              </button>
              <button
                onClick={handleExportData}
                className="px-3 py-1.5 bg-sky-50 text-sky-700 border border-sky-200 rounded-lg hover:bg-sky-100 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span className="hidden sm:inline">Xuất dữ liệu</span>
              </button>
              <button
                onClick={handleAddPhieu}
                className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 flex items-center gap-1.5 transition-all shadow-md hover:shadow-lg text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                <span>Tạo phiếu</span>
              </button>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-2 md:gap-3 mb-3">
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-lg border-l-4 border-green-500 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <p className="text-xs font-medium text-green-700">Phiếu nhập</p>
              </div>
              <p className="text-2xl font-bold text-green-900">
                {filteredStats.totalNhap}
              </p>
              {(filterNghiepVu !== 'ALL' || filterCustomer !== 'ALL' || filterDateFrom || filterDateTo) && (
                <p className="text-xs text-green-600 mt-0.5">
                  Tổng: {statistics.totalNhap}
                </p>
              )}
            </div>

            <div className="bg-gradient-to-br from-red-50 to-red-100 p-3 rounded-lg border-l-4 border-red-500 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-4 h-4 text-red-600" />
                <p className="text-xs font-medium text-red-700">Phiếu xuất</p>
              </div>
              <p className="text-2xl font-bold text-red-900">
                {filteredStats.totalXuat}
              </p>
              {(filterNghiepVu !== 'ALL' || filterCustomer !== 'ALL' || filterDateFrom || filterDateTo) && (
                <p className="text-xs text-red-600 mt-0.5">
                  Tổng: {statistics.totalXuat}
                </p>
              )}
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg border-l-4 border-blue-500 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-blue-600" />
                <p className="text-xs font-medium text-blue-700">KL Nhập</p>
              </div>
              <p className="text-xl font-bold text-blue-900">
                {formatNumber(filteredStats.totalKgNhap)} <span className="text-sm font-normal text-blue-700">kg</span>
              </p>
              <p className="text-xs text-blue-600 mt-0.5">
                {formatNumber(filteredStats.totalKienNhap)} kiện
                {(filterNghiepVu !== 'ALL' || filterCustomer !== 'ALL' || filterDateFrom || filterDateTo) && (
                  <span className="ml-1">
                    / {formatNumber(phieuList.filter(p => p['NGHIEP_VU'] === 'NHAP').reduce((sum, p) => sum + (parseFloat(p['SO KIEN']) || 0), 0))}
                  </span>
                )}
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-3 rounded-lg border-l-4 border-orange-500 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-orange-600" />
                <p className="text-xs font-medium text-orange-700">KL Xuất</p>
              </div>
              <p className="text-xl font-bold text-orange-900">
                {formatNumber(filteredStats.totalKgXuat)} <span className="text-sm font-normal text-orange-700">kg</span>
              </p>
              <p className="text-xs text-orange-600 mt-0.5">
                {formatNumber(filteredStats.totalKienXuat)} kiện
                {(filterNghiepVu !== 'ALL' || filterCustomer !== 'ALL' || filterDateFrom || filterDateTo) && (
                  <span className="ml-1">
                    / {formatNumber(phieuList.filter(p => p['NGHIEP_VU'] === 'XUAT').reduce((sum, p) => sum + (parseFloat(p['SO KIEN']) || 0), 0))}
                  </span>
                )}
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-3 rounded-lg border-l-4 border-purple-500 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center gap-2 mb-1">
                <Weight className="w-4 h-4 text-purple-600" />
                <p className="text-xs font-medium text-purple-700">KL Tồn</p>
              </div>
              <p className="text-xl font-bold text-purple-900">
                {formatNumber(statistics.totalKgNhap - statistics.totalKgXuat)} <span className="text-sm font-normal text-purple-700">kg</span>
              </p>
              <p className="text-xs text-purple-600 mt-0.5">
                {formatNumber(
                  phieuList.filter(p => p['NGHIEP_VU'] === 'NHAP').reduce((sum, p) => sum + (parseFloat(p['SO KIEN']) || 0), 0)
                  - phieuList.filter(p => p['NGHIEP_VU'] === 'XUAT').reduce((sum, p) => sum + (parseFloat(p['SO KIEN']) || 0), 0)
                )} kiện
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-3 rounded-lg border-l-4 border-yellow-500 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-yellow-600" />
                <p className="text-xs font-medium text-yellow-700">Doanh thu</p>
              </div>
              <p className="text-xl font-bold text-yellow-900">
                {new Intl.NumberFormat('vi-VN', {
                  notation: 'compact',
                  compactDisplay: 'short',
                  maximumFractionDigits: 1
                }).format(filteredStats.totalRevenue)}
              </p>
              <p className="text-xs text-yellow-600 mt-0.5 truncate">
                {formatNumber(filteredStats.totalRevenue)} VND
              </p>
            </div>
          </div>

          {/* Filters Section */}
          {showFilters && (
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-3 animate-fadeIn">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nghiệp vụ</label>
                  <select
                    value={filterNghiepVu}
                    onChange={(e) => setFilterNghiepVu(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="ALL">Tất cả</option>
                    <option value="NHAP">Nhập kho</option>
                    <option value="XUAT">Xuất kho</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Khách hàng</label>
                  <select
                    value={filterCustomer}
                    onChange={(e) => setFilterCustomer(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="ALL">Tất cả</option>
                    {uniqueCustomers.map((customer, index) => (
                      <option key={index} value={customer}>{customer}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Từ ngày</label>
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Đến ngày</label>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end mt-2">
                <button
                  onClick={() => {
                    setFilterNghiepVu('ALL');
                    setFilterCustomer('ALL');
                    setFilterDateFrom('');
                    setFilterDateTo('');
                  }}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Xóa bộ lọc
                </button>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Tìm kiếm theo số phiếu, khách hàng, người phụ trách..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Table Section */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="relative">
                  <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin"></div>
                  <div className="absolute top-0 left-0 w-12 h-12 border-4 border-transparent border-t-purple-300 rounded-full animate-pulse"></div>
                </div>
              </div>
            ) : currentItems.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Không tìm thấy phiếu nào</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gradient-to-r from-purple-50 to-purple-100 border-b border-purple-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-purple-900 cursor-pointer hover:bg-purple-200 transition-colors"
                      onClick={() => requestSort('SOPHIEU')}>
                      Số phiếu {getSortIcon('SOPHIEU')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-purple-900 cursor-pointer hover:bg-purple-200 transition-colors"
                      onClick={() => requestSort('NGHIEP_VU')}>
                      Nghiệp vụ {getSortIcon('NGHIEP_VU')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-purple-900 cursor-pointer hover:bg-purple-200 transition-colors"
                      onClick={() => requestSort('NGAYNHAP_XUAT')}>
                      Ngày {getSortIcon('NGAYNHAP_XUAT')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-purple-900">
                      Khách hàng/NCC
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-purple-900">
                      Kho
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-purple-900">
                      Người phụ trách
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-purple-900">
                      Số lượng kiện
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-purple-900 cursor-pointer hover:bg-purple-200 transition-colors"
                      onClick={() => requestSort('KL HANG')}>
                      KL hàng (KG) {getSortIcon('KL HANG')}
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-purple-900 cursor-pointer hover:bg-purple-200 transition-colors"
                      onClick={() => requestSort('THANHTIEN')}>
                      Thành tiền {getSortIcon('THANHTIEN')}
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-purple-900">
                      % VAT
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-purple-900">
                      Tiền VAT
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-purple-900 cursor-pointer hover:bg-purple-200 transition-colors"
                      onClick={() => requestSort('TONGTIEN')}>
                      Tổng tiền {getSortIcon('TONGTIEN')}
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-purple-900">
                      Thao tác
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {currentItems.map((phieu, index) => (
                    <tr key={index} className="hover:bg-purple-50 transition-colors">
                      <td className="px-3 py-2 text-xs font-medium text-gray-900">
                        {phieu['SOPHIEU']}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${phieu['NGHIEP_VU'] === 'NHAP'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {phieu['NGHIEP_VU'] === 'NHAP' ? 'Nhập' : 'Xuất'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700">
                        {convertDateYYYYMMDDtoDDMMYYYY(phieu['NGAYNHAP_XUAT'])}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700">
                        {phieu['NCC_KHACHHANG'] || '-'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700">
                        {phieu['NGHIEP_VU'] === 'NHAP' ? phieu['KHONHAP'] : phieu['KHOXUAT']}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700">
                        {phieu['NGUOIPHUTRACH'] || '-'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                          {formatNumber(phieu['SO KIEN'])}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-right font-medium text-gray-900">
                        {formatNumber(phieu['KL HANG'])}
                      </td>
                      <td className="px-3 py-2 text-xs text-right font-medium text-gray-900">
                        {formatNumber(phieu['THANHTIEN'])}
                      </td>
                      <td className="px-3 py-2 text-xs text-center text-gray-700">
                        {phieu['PT VAT'] ? `${phieu['PT VAT']}%` : '-'}
                      </td>
                      <td className="px-3 py-2 text-xs text-right text-gray-700">
                        {phieu['TIEN VAT'] ? formatNumber(phieu['TIEN VAT']) : '-'}
                      </td>
                      <td className="px-3 py-2 text-xs text-right font-medium text-gray-900">
                        {formatNumber(phieu['TONGTIEN'])}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleOpenDetailModal(phieu)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Xem chi tiết"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenPrintOptions(phieu)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="In phiếu"
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditPhieu(phieu)}
                            className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                            title="Sửa"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePhieu(phieu)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Xóa"
                          >
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {filteredPhieuList.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between mt-3 gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Hiển thị</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-xs text-gray-600">
                  / {filteredPhieuList.length} phiếu
                </span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={goToFirstPage}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Trang đầu"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Trang trước"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex gap-1">
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={index} className="px-2 py-1 text-xs text-gray-500">...</span>
                    ) : (
                      <button
                        key={index}
                        onClick={() => goToPage(page)}
                        className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${currentPage === page
                          ? 'bg-purple-500 text-white font-medium'
                          : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>

                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Trang sau"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={goToLastPage}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Trang cuối"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Phieu Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-8xl max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-purple-100">
              <h2 className="text-lg md:text-xl font-bold text-purple-900 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {isEditMode ? 'Sửa phiếu' : 'Tạo phiếu mới'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                disabled={isSubmitting}
                className="p-1.5 hover:bg-purple-200 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-3 space-y-3">
                  <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                    <h3 className="font-semibold text-purple-900 mb-3 text-sm flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Thông tin phiếu
                    </h3>

                    <div className="space-y-2.5">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Số phiếu
                        </label>
                        <input
                          type="text"
                          value={currentPhieu['SOPHIEU']}
                          onChange={(e) => handleInputChange('SOPHIEU', e.target.value)}
                          disabled={isSubmitting}
                          className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 font-medium"
                          placeholder="Tự động tạo"
                        />
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Nghiệp vụ <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={currentPhieu['NGHIEP_VU']}
                            onChange={(e) => handleInputChange('NGHIEP_VU', e.target.value)}
                            disabled={isEditMode || isSubmitting}
                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                          >
                            <option value="NHAP">Nhập kho</option>
                            <option value="XUAT">Xuất kho</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Ngày nhập/xuất <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={currentPhieu['NGAYNHAP_XUAT']}
                            onChange={(e) => handleInputChange('NGAYNHAP_XUAT', e.target.value)}
                            disabled={isSubmitting}
                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                          />
                        </div>
                      </div>

                      <div ref={customerDropdownRef}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {currentPhieu['NGHIEP_VU'] === 'NHAP' ? 'Nhà cung cấp' : 'Khách hàng'} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={customerSearchTerm}
                            onChange={(e) => handleCustomerInputChange(e.target.value)}
                            onFocus={() => setShowCustomerDropdown(true)}
                            placeholder="Nhập để tìm kiếm..."
                            disabled={isSubmitting}
                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                            required
                          />
                          {showCustomerDropdown && filteredCustomers.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                              {filteredCustomers.map((customer, idx) => (
                                <div
                                  key={idx}
                                  onClick={() => handleCustomerSelect(customer)}
                                  className="px-3 py-2 hover:bg-purple-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium text-gray-900">{customer['TEN_KHACHHANG']}</div>
                                  <div className="text-xs text-gray-500">{customer['MA_KH']}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {currentPhieu['NGHIEP_VU'] === 'NHAP' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Kho nhập <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={currentPhieu['KHONHAP']}
                            onChange={(e) => handleInputChange('KHONHAP', e.target.value)}
                            disabled={isSubmitting}
                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                          >
                            <option value="">-- Chọn kho --</option>
                            {khoList.map((kho, idx) => (
                              <option key={idx} value={kho['KHO']}>
                                {kho['KHO']}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {currentPhieu['NGHIEP_VU'] === 'XUAT' && (
                        <>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Kho xuất <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={currentPhieu['KHOXUAT']}
                              onChange={(e) => handleInputChange('KHOXUAT', e.target.value)}
                              disabled={isSubmitting}
                              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                            >
                              <option value="">-- Chọn kho --</option>
                              {khoList.map((kho, idx) => (
                                <option key={idx} value={kho['KHO']}>
                                  {kho['KHO']}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              KL xe và hàng (KG) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              value={klXeVaHang}
                              onChange={(e) => handleKlXeVaHangChange(e.target.value)}
                              disabled={isSubmitting}
                              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                              placeholder="0"
                            />
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                KL xe (KG) <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="number"
                                value={klXe}
                                onChange={(e) => handleKlXeChange(e.target.value)}
                                disabled={isSubmitting}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                                placeholder="0"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                KL hàng (KG)
                              </label>
                              <input
                                type="number"
                                value={klHang}
                                readOnly
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg bg-gray-100 font-medium"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Số kiện <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="number"
                                value={soKien}
                                onChange={(e) => handleSoKienChange(e.target.value)}
                                disabled={isSubmitting}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                                placeholder="0"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Số KG TB/kiện
                              </label>
                              <input
                                type="number"
                                value={soKgTbKien}
                                readOnly
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg bg-gray-100 font-medium"
                              />
                            </div>
                          </div>
                        </>
                      )}

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Người phụ trách
                        </label>
                        <input
                          type="text"
                          value={currentPhieu['NGUOIPHUTRACH']}
                          onChange={(e) => handleInputChange('NGUOIPHUTRACH', e.target.value)}
                          disabled={isSubmitting}
                          className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                          placeholder="Nhập tên người phụ trách"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Diễn giải
                        </label>
                        <textarea
                          value={currentPhieu['DIENGIAI']}
                          onChange={(e) => handleInputChange('DIENGIAI', e.target.value)}
                          disabled={isSubmitting}
                          rows={3}
                          className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 resize-none"
                          placeholder="Ghi chú thêm..."
                        />
                      </div>

                      {/* Hiển thị thông tin tài chính - CHO XUẤT KHO */}
                      {currentPhieu['NGHIEP_VU'] === 'XUAT' && chiTietList.length > 0 && (
                        <div className="bg-white p-2.5 rounded-lg border border-purple-300 mt-3">
                          <p className="text-xs font-semibold text-purple-900 mb-2">Thông tin tài chính:</p>

                          <div className="flex justify-between items-center text-xs mb-1.5 border-b border-gray-100 pb-1.5">
                            <span className="text-gray-600">Thành tiền:</span>
                            <span className="font-bold text-gray-900">
                              {formatNumber(currentPhieu['THANHTIEN'])}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs mb-1.5 border-b border-gray-100 pb-1.5">
                            <span className="text-gray-600 flex items-center gap-1">
                              <span>VAT (%):</span>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={currentPhieu['PT VAT']}
                                onChange={(e) => handleInputChange('PT VAT', e.target.value)}
                                disabled={isSubmitting}
                                className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 font-normal"
                              />
                            </span>
                            <span className="font-bold text-gray-900">
                              {formatNumber(currentPhieu['TIEN VAT'])}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-600 font-bold">Tổng tiền:</span>
                            <span className="font-bold text-purple-900 text-sm">
                              {formatNumber(currentPhieu['TONGTIEN'])}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Hiển thị thông tin tổng - CHO XUẤT KHO */}
                      {currentPhieu['NGHIEP_VU'] === 'XUAT' && chiTietList.length > 0 && (
                        <div className="bg-white p-2.5 rounded-lg border border-purple-300 mt-3">
                          <p className="text-xs font-semibold text-purple-900 mb-2">Kiểm tra dữ liệu:</p>

                          {/* Kiểm tra số kiện */}
                          <div className="mb-3 pb-3 border-b border-purple-200">
                            <div className="flex justify-between items-center text-xs mb-1.5">
                              <span className="text-gray-600">Số kiện header:</span>
                              <span className="font-bold text-purple-900">
                                {formatNumber(soKien)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center text-xs mb-1.5">
                              <span className="text-gray-600">Tổng số kiện chi tiết:</span>
                              <span className={`font-bold ${Math.abs(chiTietTotals.chenhLechSoKien) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                                {formatNumber(chiTietTotals.tongSoKien)}
                              </span>
                            </div>

                            {Math.abs(chiTietTotals.chenhLechSoKien) > 0.01 && (
                              <div className={`mt-2 p-2 rounded flex items-start gap-2 ${chiTietTotals.chenhLechSoKien > 0
                                ? 'bg-red-50 border border-red-200'
                                : 'bg-orange-50 border border-orange-200'
                                }`}>
                                <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${chiTietTotals.chenhLechSoKien > 0 ? 'text-red-600' : 'text-orange-600'
                                  }`} />
                                <div>
                                  <p className={`text-xs font-semibold ${chiTietTotals.chenhLechSoKien > 0 ? 'text-red-700' : 'text-orange-700'
                                    }`}>
                                    {chiTietTotals.chenhLechSoKien > 0
                                      ? `❌ VƯỢT ${formatNumber(chiTietTotals.chenhLechSoKien)} kiện`
                                      : `⚠️ THIẾU ${formatNumber(Math.abs(chiTietTotals.chenhLechSoKien))} kiện`
                                    }
                                  </p>
                                </div>
                              </div>
                            )}

                            {Math.abs(chiTietTotals.chenhLechSoKien) <= 0.01 && (
                              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <p className="text-xs font-semibold text-green-700">✓ Số kiện khớp chính xác</p>
                              </div>
                            )}
                          </div>

                          {/* Kiểm tra khối lượng */}
                          <div>
                            <div className="flex justify-between items-center text-xs mb-1.5">
                              <span className="text-gray-600">KL hàng header:</span>
                              <span className="font-bold text-purple-900">
                                {formatNumber(klHang)} kg
                              </span>
                            </div>
                            <div className="flex justify-between items-center text-xs mb-1.5">
                              <span className="text-gray-600">Tổng KL chi tiết:</span>
                              <span className={`font-bold ${Math.abs(chiTietTotals.chenhLechKhoiLuong) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                                {formatNumber(chiTietTotals.tongKhoiLuong)} kg
                              </span>
                            </div>

                            {Math.abs(chiTietTotals.chenhLechKhoiLuong) > 0.01 && (
                              <div className={`mt-2 p-2 rounded flex items-start gap-2 ${chiTietTotals.chenhLechKhoiLuong > 0
                                ? 'bg-red-50 border border-red-200'
                                : 'bg-orange-50 border border-orange-200'
                                }`}>
                                <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${chiTietTotals.chenhLechKhoiLuong > 0 ? 'text-red-600' : 'text-orange-600'
                                  }`} />
                                <div>
                                  <p className={`text-xs font-semibold ${chiTietTotals.chenhLechKhoiLuong > 0 ? 'text-red-700' : 'text-orange-700'
                                    }`}>
                                    {chiTietTotals.chenhLechKhoiLuong > 0
                                      ? `❌ VƯỢT ${formatNumber(chiTietTotals.chenhLechKhoiLuong)} kg`
                                      : `⚠️ THIẾU ${formatNumber(Math.abs(chiTietTotals.chenhLechKhoiLuong))} kg`
                                    }
                                  </p>
                                  <p className={`text-xs mt-1 ${chiTietTotals.chenhLechKhoiLuong > 0 ? 'text-red-600' : 'text-orange-600'
                                    }`}>
                                    {chiTietTotals.chenhLechKhoiLuong > 0
                                      ? 'Giảm số kiện hoặc giảm KL hàng header'
                                      : 'Thêm số kiện hoặc tăng KL hàng header'
                                    }
                                  </p>
                                </div>
                              </div>
                            )}

                            {Math.abs(chiTietTotals.chenhLechKhoiLuong) <= 0.01 && (
                              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <p className="text-xs font-semibold text-green-700">✓ Khối lượng khớp chính xác</p>
                              </div>
                            )}
                          </div>

                          {/* Tổng kết */}
                          {Math.abs(chiTietTotals.chenhLechSoKien) <= 0.01 &&
                            Math.abs(chiTietTotals.chenhLechKhoiLuong) <= 0.01 && (
                              <div className="mt-3 pt-3 border-t border-green-200">
                                <div className="p-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300 rounded-lg flex items-center gap-2">
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                  <p className="text-xs font-bold text-green-800">
                                    ✓ Dữ liệu hợp lệ - Có thể lưu phiếu
                                  </p>
                                </div>
                              </div>
                            )}
                        </div>
                      )}

                      {/* Hiển thị cho NHẬP KHO */}
                      {currentPhieu['NGHIEP_VU'] === 'NHAP' && chiTietList.length > 0 && (
                        <div className="bg-white p-2.5 rounded-lg border border-purple-300 mt-3">
                          <p className="text-xs font-semibold text-purple-900 mb-2">Tổng chi tiết:</p>

                          <div className="flex justify-between items-center text-xs mb-1.5">
                            <span className="text-gray-600">Số kiện:</span>
                            <span className="font-bold text-purple-900">
                              {formatNumber(chiTietTotals.tongSoKien)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-xs mb-1.5">
                            <span className="text-gray-600">Khối lượng:</span>
                            <span className="font-bold text-purple-900">
                              {formatNumber(chiTietTotals.tongKhoiLuong)} kg
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-600">KG TB/kiện:</span>
                            <span className="font-bold text-purple-900">
                              {formatNumber(chiTietTotals.tongSoKien > 0 ? chiTietTotals.tongKhoiLuong / chiTietTotals.tongSoKien : 0)}
                            </span>
                          </div>

                          <div className="mt-3 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-300 rounded-lg flex items-center gap-2">
                            <Info className="w-5 h-5 text-blue-600" />
                            <p className="text-xs font-semibold text-blue-800">
                              Dữ liệu sẽ được tự động cập nhật
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-9 space-y-3">
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-blue-900 text-sm flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Chi tiết phiếu ({chiTietList.length} dòng)
                      </h3>

                      {/* Nút thêm dòng mới */}
                      <button
                        onClick={handleAddEmptyRow}
                        disabled={isSubmitting}
                        className="px-2 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-4 h-4" />
                        Thêm dòng mới
                      </button>
                    </div>

                    {chiTietList.length === 0 ? (
                      <div className="text-center py-6 text-gray-500 text-sm bg-white rounded-lg border border-blue-200 mb-3">
                        Chưa có chi tiết. Thêm dòng mới bên dưới.
                      </div>
                    ) : (
                      <div className="overflow-x-auto bg-white rounded-lg border border-blue-200 mb-3">
                        <table className="w-full text-sm">
                          <thead className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">STT</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">Nhóm hàng</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">Tiêu chuẩn</th>
                              {currentPhieu['NGHIEP_VU'] === 'NHAP' && (
                                <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">Đội hàng khô</th>
                              )}
                              <th className="px-3 py-2 text-xs font-semibold text-blue-900">Số kiện</th>
                              {currentPhieu['NGHIEP_VU'] === 'NHAP' && (
                                <>
                                  <th className="px-3 py-2 text-right text-xs font-semibold text-blue-900">KG TB/kiện</th>
                                  <th className="px-3 py-2 text-right text-xs font-semibold text-blue-900">KG TB/M3</th>
                                  <th className="px-3 py-2 text-right text-xs font-semibold text-blue-900">Tổng KG UL</th>
                                  <th className="px-3 py-2 text-right text-xs font-semibold text-blue-900">Tổng M3 UL</th>
                                </>
                              )}
                              {currentPhieu['NGHIEP_VU'] === 'XUAT' && (
                                <th className="px-3 py-2 text-right text-xs font-semibold text-blue-900">Khối lượng (kg)</th>
                              )}
                              <th className="px-3 py-2 text-xs font-semibold text-blue-900">Đơn giá/Kg</th>
                              <th className="px-3 py-2 text-right text-xs font-semibold text-blue-900">Thành tiền</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">Ghi chú</th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-blue-900">Thao tác</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {chiTietList.map((item, index) => (
                              <tr key={index} className="hover:bg-blue-50">
                                <td className="px-3 py-2 text-xs text-gray-700">{index + 1}</td>
                                <td className="px-2 py-1">
                                  <input
                                    type="text"
                                    value={item['NHOM_HANG']}
                                    readOnly
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-gray-100"
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <select
                                    value={item['TIEU_CHUAN']}
                                    onChange={(e) => handleUpdateChiTiet(index, 'TIEU_CHUAN', e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="">-- Chọn --</option>
                                    {tieuChuanList.map((tc, idx) => (
                                      <option key={idx} value={tc['TIEU_CHUAN']}>{tc['TIEU_CHUAN']}</option>
                                    ))}
                                  </select>
                                </td>
                                {currentPhieu['NGHIEP_VU'] === 'NHAP' && (
                                  <td className="px-2 py-1">
                                    <select
                                      value={item['DOI_HANG_KHO']}
                                      onChange={(e) => handleUpdateChiTiet(index, 'DOI_HANG_KHO', e.target.value)}
                                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value="">-- Chọn --</option>
                                      {doiHangKhoList.map((dhk, idx) => (
                                        <option key={idx} value={dhk['DOI_HANG_KHO']}>{dhk['DOI_HANG_KHO']}</option>
                                      ))}
                                    </select>
                                  </td>
                                )}
                                <td className="px-2 py-1">
                                  <input
                                    type="number"
                                    value={item['SO KIEN']}
                                    onChange={(e) => handleUpdateChiTiet(index, 'SO KIEN', e.target.value)}
                                    className="w-20 px-2 py-1 text-xs text-right border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                </td>
                                {currentPhieu['NGHIEP_VU'] === 'NHAP' && (
                                  <>
                                    <td className="px-2 py-1">
                                      <input
                                        type="number"
                                        value={item['SO KG TB/KIEN']}
                                        onChange={(e) => handleUpdateChiTiet(index, 'SO KG TB/KIEN', e.target.value)}
                                        className="w-20 px-2 py-1 text-xs text-right border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                      />
                                    </td>
                                    <td className="px-2 py-1">
                                      <input
                                        type="number"
                                        value={item['SO KG TB/M3']}
                                        onChange={(e) => handleUpdateChiTiet(index, 'SO KG TB/M3', e.target.value)}
                                        className="w-20 px-2 py-1 text-xs text-right border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-xs text-right text-gray-700 bg-gray-50 font-medium">{formatNumber(item['TONG KG UL'])}</td>
                                    <td className="px-3 py-2 text-xs text-right text-gray-700 bg-gray-50">{formatNumber(item['TONG M3 UL'])}</td>
                                  </>
                                )}
                                {currentPhieu['NGHIEP_VU'] === 'XUAT' && (
                                  <td className="px-3 py-2 text-xs text-right text-gray-700 bg-gray-50 font-medium">{formatNumber(item['TONG KG UL'])}</td>
                                )}
                                <td className="px-2 py-1">
                                  <input
                                    type="number"
                                    value={item['DONGIA']}
                                    onChange={(e) => handleUpdateChiTiet(index, 'DONGIA', e.target.value)}
                                    className="w-24 px-2 py-1 text-xs text-right border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="px-3 py-2 text-xs text-right font-medium text-gray-900 bg-gray-50">{formatNumber(item['THANHTIEN'])}</td>
                                <td className="px-2 py-1">
                                  <input
                                    type="text"
                                    value={item['GHICHU'] || ''}
                                    onChange={(e) => handleUpdateChiTiet(index, 'GHICHU', e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                    placeholder="Ghi chú..."
                                  />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <button
                                    onClick={() => handleDeleteChiTiet(index)}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Xóa"
                                  >
                                    <Trash className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {/* Dòng tổng */}
                            <tr className="bg-gradient-to-r from-blue-100 to-blue-50 font-semibold">
                              <td colSpan={currentPhieu['NGHIEP_VU'] === 'NHAP' ? 4 : 3} className="px-3 py-2 text-xs text-right text-blue-900">
                                TỔNG CỘNG:
                              </td>
                              <td className="px-3 py-2 text-xs text-right text-blue-900">
                                {formatNumber(chiTietTotals.tongSoKien)}
                              </td>
                              {currentPhieu['NGHIEP_VU'] === 'NHAP' && (
                                <>
                                  <td className="px-3 py-2"></td>
                                  <td className="px-3 py-2"></td>
                                  <td className="px-3 py-2 text-xs text-right text-blue-900">
                                    {formatNumber(chiTietTotals.tongKhoiLuong)}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-right text-blue-900">
                                    {formatNumber(chiTietList.reduce((sum, item) => sum + (parseFloat(item['TONG M3 UL']) || 0), 0))}
                                  </td>
                                </>
                              )}
                              {currentPhieu['NGHIEP_VU'] === 'XUAT' && (
                                <td className="px-3 py-2 text-xs text-right text-blue-900">
                                  {formatNumber(chiTietTotals.tongKhoiLuong)}
                                </td>
                              )}
                              <td className="px-3 py-2"></td>
                              <td className="px-3 py-2 text-xs text-right text-blue-900">
                                {formatNumber(chiTietList.reduce((sum, item) => sum + (parseFloat(item['THANHTIEN']) || 0), 0))}
                              </td>
                              <td colSpan={2}></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer buttons */}
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowModal(false)}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                onClick={handleSubmitPhieu}
                disabled={
                  isSubmitting ||
                  (currentPhieu['NGHIEP_VU'] === 'XUAT' && chiTietList.length > 0 && (
                    Math.abs(chiTietTotals.chenhLechSoKien) > 0.01 ||
                    Math.abs(chiTietTotals.chenhLechKhoiLuong) > 0.01
                  ))
                }
                className={`px-4 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 ${isSubmitting ||
                  (currentPhieu['NGHIEP_VU'] === 'XUAT' && chiTietList.length > 0 && (
                    Math.abs(chiTietTotals.chenhLechSoKien) > 0.01 ||
                    Math.abs(chiTietTotals.chenhLechKhoiLuong) > 0.01
                  ))
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700'
                  }`}
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Đang lưu...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {isEditMode ? 'Cập nhật' : 'Lưu phiếu'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Xem Chi Tiết */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
              <h2 className="text-lg md:text-xl font-bold text-blue-900 flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Chi Tiết Phiếu - {currentPhieu['SOPHIEU']}
              </h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-1.5 hover:bg-blue-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-2 text-sm">Thông tin chung</h3>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Số phiếu:</span>
                      <span className="font-medium text-gray-900">{currentPhieu['SOPHIEU']}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Nghiệp vụ:</span>
                      <span className={`font-medium ${currentPhieu['NGHIEP_VU'] === 'NHAP' ? 'text-green-700' : 'text-red-700'}`}>
                        {currentPhieu['NGHIEP_VU'] === 'NHAP' ? 'Nhập kho' : 'Xuất kho'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ngày:</span>
                      <span className="font-medium text-gray-900">{convertDateYYYYMMDDtoDDMMYYYY(currentPhieu['NGAYNHAP_XUAT'])}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{currentPhieu['NGHIEP_VU'] === 'NHAP' ? 'NCC' : 'Khách hàng'}:</span>
                      <span className="font-medium text-gray-900">{currentPhieu['NCC_KHACHHANG'] || '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <h3 className="font-semibold text-purple-900 mb-2 text-sm">Thông tin kho</h3>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Kho:</span>
                      <span className="font-medium text-gray-900">
                        {currentPhieu['NGHIEP_VU'] === 'NHAP' ? currentPhieu['KHONHAP'] : currentPhieu['KHOXUAT']}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Người phụ trách:</span>
                      <span className="font-medium text-gray-900">{currentPhieu['NGUOIPHUTRACH'] || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Số kiện:</span>
                      <span className="font-medium text-gray-900">{formatNumber(soKien)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">KL hàng:</span>
                      <span className="font-medium text-gray-900">{formatNumber(klHang)} kg</span>
                    </div>
                    {currentPhieu['NGHIEP_VU'] === 'XUAT' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">KL xe và hàng:</span>
                          <span className="font-medium text-gray-900">{formatNumber(klXeVaHang)} kg</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">KL xe:</span>
                          <span className="font-medium text-gray-900">{formatNumber(klXe)} kg</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {currentPhieu['DIENGIAI'] && (
                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 mb-4">
                  <h3 className="font-semibold text-yellow-900 mb-1 text-sm">Diễn giải</h3>
                  <p className="text-xs text-gray-700">{currentPhieu['DIENGIAI']}</p>
                </div>
              )}

              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2 text-sm">Danh sách chi tiết ({chiTietList.length} dòng)</h3>
                <div className="overflow-x-auto rounded-lg border border-gray-300">
                  <table className="w-full text-xs bg-white">
                    <thead className="bg-gray-100 border-b border-gray-300">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-900">STT</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-900">Nhóm hàng</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-900">Tiêu chuẩn</th>
                        {currentPhieu['NGHIEP_VU'] === 'NHAP' && (
                          <th className="px-2 py-1.5 text-left font-semibold text-gray-900">Đội hàng khô</th>
                        )}
                        <th className="px-2 py-1.5 text-right font-semibold text-gray-900">Số kiện</th>
                        <th className="px-2 py-1.5 text-right font-semibold text-gray-900">Khối lượng (kg)</th>
                        <th className="px-2 py-1.5 text-right font-semibold text-gray-900">Đơn giá/Kg</th>
                        <th className="px-2 py-1.5 text-right font-semibold text-gray-900">Thành tiền</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-900">Ghi chú</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {chiTietList.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-2 py-1.5">{index + 1}</td>
                          <td className="px-2 py-1.5 text-gray-700">{item['NHOM_HANG']}</td>
                          <td className="px-2 py-1.5 text-gray-700">{item['TIEU_CHUAN'] || '-'}</td>
                          {currentPhieu['NGHIEP_VU'] === 'NHAP' && (
                            <td className="px-2 py-1.5 text-gray-700">{item['DOI_HANG_KHO'] || '-'}</td>
                          )}
                          <td className="px-2 py-1.5 text-right font-medium text-gray-900">{formatNumber(item['SO KIEN'])}</td>
                          <td className="px-2 py-1.5 text-right font-medium text-gray-900">{formatNumber(item['TONG KG UL'])}</td>
                          <td className="px-2 py-1.5 text-right text-gray-700">{formatNumber(item['DONGIA'])}</td>
                          <td className="px-2 py-1.5 text-right font-medium text-gray-900">{formatNumber(item['THANHTIEN'])}</td>
                          <td className="px-2 py-1.5 text-gray-700">{item['GHICHU'] || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => handleOpenPrintOptions(currentPhieu)}
                className="px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 transition-colors"
              >
                <Printer className="w-4 h-4" />
                In phiếu
              </button>
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Tùy Chọn In Phiếu Xuất */}
      {showPrintOptions && phieuToPrint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mx-auto mb-4">
                <Printer className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                Tùy chọn in phiếu
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Phiếu: <strong>{phieuToPrint['SOPHIEU']}</strong>
              </p>

              {/* Tùy chọn chọn công ty */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Chọn công ty:
                </label>
                <div className="space-y-2">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="radio"
                      name="company"
                      value="HOANG_NGUYEN_LONG"
                      checked={selectedCompany === 'HOANG_NGUYEN_LONG'}
                      onChange={(e) => setSelectedCompany(e.target.value)}
                      className="w-4 h-4 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 mt-0.5"
                    />
                    <span className="ml-3 text-sm text-gray-900">
                      CÔNG TY CP TM SX XD HOÀNG NGUYÊN LONG
                    </span>
                  </label>
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="radio"
                      name="company"
                      value="HOANG_KHOI"
                      checked={selectedCompany === 'HOANG_KHOI'}
                      onChange={(e) => setSelectedCompany(e.target.value)}
                      className="w-4 h-4 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 mt-0.5"
                    />
                    <div className="ml-3 text-sm text-gray-900">
                      <div>CÔNG TY TNHH GỖ HOÀNG KHÔI</div>
                      <div className="text-xs text-gray-600">MST: 3702896657</div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={printWithPrice}
                    onChange={(e) => setPrintWithPrice(e.target.checked)}
                    className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-2 focus:ring-green-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-900">
                    Hiển thị đơn giá và thành tiền
                  </span>
                </label>
                <p className="ml-8 mt-1 text-xs text-gray-500">
                  {printWithPrice
                    ? '✓ Phiếu sẽ hiển thị cột đơn giá và thành tiền'
                    : '○ Phiếu chỉ hiển thị thông tin hàng hóa'}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowPrintOptions(false);
                    setPrintWithPrice(false);
                    setSelectedCompany('HOANG_NGUYEN_LONG');
                  }}
                  className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Hủy
                </button>
                <button
                  onClick={() => handlePrintPhieu(phieuToPrint, printWithPrice, selectedCompany)}
                  className="flex-1 px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 transition-colors font-medium"
                >
                  <Printer className="w-4 h-4" />
                  In phiếu
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Quản Lý Tiêu Chuẩn */}
      {showTieuChuanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
              <h2 className="text-lg md:text-xl font-bold text-blue-900 flex items-center gap-2">
                <Info className="w-5 h-5" />
                Quản Lý Tiêu Chuẩn
              </h2>
              <button
                onClick={() => setShowTieuChuanModal(false)}
                className="p-1.5 hover:bg-blue-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
                <h3 className="font-semibold text-blue-900 mb-2 text-sm">Thêm tiêu chuẩn mới</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTieuChuan}
                    onChange={(e) => setNewTieuChuan(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTieuChuan()}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập tên tiêu chuẩn..."
                  />
                  <button
                    onClick={handleAddTieuChuan}
                    className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 transition-colors font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Thêm
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2 text-sm">
                  Danh sách tiêu chuẩn ({tieuChuanList.length})
                </h3>
                {tieuChuanList.length === 0 ? (
                  <div className="text-center py-8">
                    <Info className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Chưa có tiêu chuẩn nào</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {tieuChuanList.map((tc, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-blue-700">{index + 1}</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{tc['TIEU_CHUAN']}</span>
                        </div>
                        <button
                          onClick={() => handleDeleteTieuChuan(tc['ID_TC'])}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Xóa tiêu chuẩn"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowTieuChuanModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Quản Lý Đội Hàng Khô */}
      {showDoiHangKhoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-indigo-100">
              <h2 className="text-lg md:text-xl font-bold text-indigo-900 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Quản Lý Đội Hàng Khô
              </h2>
              <button
                onClick={() => setShowDoiHangKhoModal(false)}
                className="p-1.5 hover:bg-indigo-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-200 mb-4">
                <h3 className="font-semibold text-indigo-900 mb-2 text-sm">Thêm đội hàng khô mới</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newDoiHangKho}
                    onChange={(e) => setNewDoiHangKho(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddDoiHangKho()}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Nhập tên đội hàng khô..."
                  />
                  <button
                    onClick={handleAddDoiHangKho}
                    className="px-4 py-2 text-sm bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 flex items-center gap-2 transition-colors font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Thêm
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2 text-sm">
                  Danh sách đội hàng khô ({doiHangKhoList.length})
                </h3>
                {doiHangKhoList.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Chưa có đội hàng khô nào</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {doiHangKhoList.map((dhk, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200 hover:border-indigo-300 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-indigo-700">{index + 1}</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{dhk['DOI_HANG_KHO']}</span>
                        </div>
                        <button
                          onClick={() => handleDeleteDoiHangKho(dhk['ID_DHK'])}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Xóa đội hàng khô"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowDoiHangKhoModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Excel Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-orange-100">
              <h2 className="text-lg md:text-xl font-bold text-orange-900 flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Import Dữ Liệu Từ Excel
              </h2>
              <button
                onClick={handleCloseImportModal}
                className="p-1.5 hover:bg-orange-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Nút tải mẫu */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
                <div className="flex items-start gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-900 text-sm mb-1">
                      Tải file mẫu Excel
                    </h3>
                    <p className="text-xs text-green-700 mb-3">
                      Tải file mẫu để biết định dạng dữ liệu cần import. File mẫu có 2 sheet:<br />
                      • <strong>Mẫu Nhập Kho Domino</strong>: Dành cho nhập kho<br />
                      • <strong>Mẫu Xuất Kho Domino</strong>: Dành cho xuất kho
                    </p>
                    <button
                      onClick={handleExportTemplate}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 transition-colors text-sm font-medium shadow-sm hover:shadow"
                    >
                      <Download className="w-4 h-4" />
                      Tải file mẫu Excel
                    </button>
                  </div>
                </div>
              </div>

              {/* Chọn nghiệp vụ */}
              <div className="bg-orange-50 p-3 rounded-lg border border-orange-200 mb-4">
                <label className="block text-sm font-medium text-orange-900 mb-2">
                  Chọn loại phiếu <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="NHAP"
                      checked={importNghiepVu === 'NHAP'}
                      onChange={(e) => {
                        setImportNghiepVu(e.target.value);
                        setImportPreview([]);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="w-4 h-4 text-orange-600 border-gray-300 focus:ring-2 focus:ring-orange-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-900">Nhập kho</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="XUAT"
                      checked={importNghiepVu === 'XUAT'}
                      onChange={(e) => {
                        setImportNghiepVu(e.target.value);
                        setImportPreview([]);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="w-4 h-4 text-orange-600 border-gray-300 focus:ring-2 focus:ring-orange-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-900">Xuất kho</span>
                  </label>
                </div>
              </div>

              {/* Chọn file */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
                <label className="block text-sm font-medium text-blue-900 mb-2">
                  Chọn file Excel <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="flex-1 text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600"
                  />
                  {importFile && (
                    <button
                      onClick={() => {
                        setImportFile(null);
                        setImportPreview([]);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Xóa file"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                {importFile && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span>Đã chọn: {importFile.name}</span>
                  </div>
                )}
              </div>

              {/* Preview data */}
              {importPreview.length > 0 && (() => {
                const validRows = importPreview.filter(r => r.errors.length === 0);
                const errorRows = importPreview.filter(r => r.errors.length > 0);

                // Group theo phiếu
                const groupedPreview = {};
                validRows.forEach(row => {
                  const key = `${row.ngayYYYYMMDD}_${row.nccKhachHang}_${row.kho}_${row.nguoiPhuTrach}`;
                  if (!groupedPreview[key]) {
                    groupedPreview[key] = {
                      ngay: row.ngay,
                      nccKhachHang: row.nccKhachHang,
                      kho: row.kho,
                      nguoiPhuTrach: row.nguoiPhuTrach,
                      chiTiet: []
                    };
                  }
                  groupedPreview[key].chiTiet.push(row);
                });

                const phieuCount = Object.keys(groupedPreview).length;

                return (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900 text-sm">
                        Xem trước: {phieuCount} phiếu - {validRows.length} dòng
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          {validRows.length} hợp lệ
                        </span>
                        <span className="text-xs text-red-600 flex items-center gap-1">
                          <XCircle className="w-4 h-4" />
                          {errorRows.length} lỗi
                        </span>
                      </div>
                    </div>

                    {/* Hiển thị lỗi */}
                    {errorRows.length > 0 && (
                      <div className="mb-4 bg-red-50 p-3 rounded-lg border border-red-200">
                        <h4 className="font-semibold text-red-900 text-xs mb-2">
                          ⚠️ {errorRows.length} dòng có lỗi:
                        </h4>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {errorRows.map((row, idx) => (
                            <div key={idx} className="text-xs text-red-700">
                              <strong>Dòng {row.rowIndex}:</strong> {row.errors.join(', ')}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hiển thị preview từng phiếu */}
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {Object.keys(groupedPreview).map((key, phieuIndex) => {
                        const phieu = groupedPreview[key];
                        const tongSoKien = phieu.chiTiet.reduce((sum, item) =>
                          sum + (importNghiepVu === 'NHAP' ? item.soKien : item.soKienChiTiet), 0
                        );
                        const tongKhoiLuong = phieu.chiTiet.reduce((sum, item) => sum + (item.tongKgUl || 0), 0);
                        const tongTien = phieu.chiTiet.reduce((sum, item) => sum + (item.thanhTien || 0), 0);

                        return (
                          <div key={phieuIndex} className="bg-white rounded-lg border border-blue-300 overflow-hidden">
                            <div className="bg-blue-100 px-3 py-2 border-b border-blue-300">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-500 text-white rounded-full text-xs font-bold">
                                    {phieuIndex + 1}
                                  </span>
                                  <div>
                                    <div className="font-semibold text-blue-900 text-sm">
                                      {phieu.ngay} - {phieu.nccKhachHang}
                                    </div>
                                    <div className="text-xs text-blue-700">
                                      {phieu.kho} - {phieu.chiTiet.length} dòng
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-xs text-blue-700">
                                    {formatNumber(tongSoKien)} kiện
                                  </div>
                                  <div className="text-xs font-semibold text-blue-900">
                                    {formatNumber(tongKhoiLuong)} kg
                                  </div>
                                  {importNghiepVu === 'XUAT' && (
                                    <div className="text-xs font-semibold text-blue-900">
                                      {formatNumber(tongTien)} VND
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                  <tr>
                                    <th className="px-2 py-1.5 text-left font-semibold text-gray-900">STT</th>
                                    <th className="px-2 py-1.5 text-left font-semibold text-gray-900">Nhóm hàng</th>
                                    <th className="px-2 py-1.5 text-left font-semibold text-gray-900">Tiêu chuẩn</th>
                                    <th className="px-2 py-1.5 text-right font-semibold text-gray-900">Số kiện</th>
                                    <th className="px-2 py-1.5 text-right font-semibold text-gray-900">KL (kg)</th>
                                    <th className="px-2 py-1.5 text-right font-semibold text-gray-900">Đơn giá/Kg</th>
                                    <th className="px-2 py-1.5 text-right font-semibold text-gray-900">Thành tiền</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {phieu.chiTiet.map((item, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                      <td className="px-2 py-1.5">{idx + 1}</td>
                                      <td className="px-2 py-1.5 text-gray-700">{item.nhomHang}</td>
                                      <td className="px-2 py-1.5 text-gray-700">{item.tieuChuan || '-'}</td>
                                      <td className="px-2 py-1.5 text-right font-medium text-gray-900">
                                        {formatNumber(importNghiepVu === 'NHAP' ? item.soKien : item.soKienChiTiet)}
                                      </td>
                                      <td className="px-2 py-1.5 text-right font-medium text-gray-900">
                                        {formatNumber(item.tongKgUl)}
                                      </td>
                                      <td className="px-2 py-1.5 text-right text-gray-700">
                                        {formatNumber(item.donGia)}
                                      </td>
                                      <td className="px-2 py-1.5 text-right font-medium text-gray-900">
                                        {formatNumber(item.thanhTien)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Hướng dẫn */}
              {importPreview.length === 0 && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-semibold mb-2">Hướng dẫn import:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Tải file mẫu Excel bằng nút "Tải file mẫu Excel" ở trên</li>
                        <li>Điền dữ liệu vào file mẫu theo đúng định dạng</li>
                        <li>Chọn loại phiếu (Nhập kho hoặc Xuất kho)</li>
                        <li>Chọn file Excel đã điền dữ liệu</li>
                        <li>Kiểm tra xem trước và nhấn "Import"</li>
                      </ol>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleCloseImportModal}
                disabled={isImporting}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Hủy
              </button>
              <button
                onClick={handleImportData}
                disabled={isImporting || importPreview.length === 0 || importPreview.some(r => r.errors.length > 0)}
                className="px-4 py-2 text-sm bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isImporting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Đang import...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Import dữ liệu
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                Xác nhận xóa phiếu
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Bạn có chắc chắn muốn xóa phiếu <strong>{phieuToDelete?.['SOPHIEU']}</strong>?
                <br />
                Hành động này không thể hoàn tác.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (!isSubmitting) {
                      setShowDeleteConfirmation(false);
                      setPhieuToDelete(null);
                    }
                  }}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Hủy
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Đang xóa...
                    </>
                  ) : (
                    <>
                      <Trash className="w-4 h-4" />
                      Xóa
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default XuatNhapKhoDominoManagement;